import MaterialRequestUpdates from './MaterialRequestUpdates';
import GenericItemPicker from './GenericItemPicker';
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Tab, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import AdminSectionNav from './AdminSectionNav';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { countries, getStatesForCountry, getOrgAddressDefaults, matchIndianState } from '../utils/countriesStates';
import GstinLookupInput from './GstinLookupInput';
import { GstApiSettingsCard } from './GstRegistration';
import FeatureNotice from './FeatureNotice';
import DataImportExport from './DataImportExport';
import { loadOrgAddressDefaults } from '../utils/orgAddressDefaults';
import { formatRoleName, getRoleLabel, isOrgAdminRole } from '../utils/roleUtils';
import OrgPrintChrome from './OrgPrintChrome';
import OrganizationQualityGrades from './OrganizationQualityGrades';
import PhoneNumberInput, { isValidPhoneNumber } from './PhoneNumberInput';
import HsnLookupInput from './HsnLookupInput';
import CategorySelect from './CategorySelect';
import AttributeFields from './MaterialAttributes';
import PlanComparison from './PlanComparison';
import OpeningStockFields, { emptyOpeningStock, openingStockProblem, saveOpeningStock, useOpeningStockInfo } from './OpeningStockFields';
import MaterialCatalogBrowser from './MaterialCatalogBrowser';
import SimilarItemsHint from './SimilarItemsHint';
import { findItemTypeForHsn } from '../utils/hsnSuggestions';
import useSectionTab from '../hooks/useSectionTab';
import WebAddressInput, { checkWebAddress } from './WebAddressInput';
import EditLog from './EditLog';
import HistoryButton from './HistoryButton';
import ProductPhotos from './ProductPhotos';
import OnlineStoreSettings from './OnlineStoreSettings';
import PasswordHint from './PasswordHint';
import { passwordProblem } from '../utils/passwordPolicy';


import { DATE_FORMATS, defaultDateFormat, formatDate, formatDateTime } from '../utils/dateFormat';
const ORG_ADMIN_SECTIONS = [
  {
    items: [
      { key: 'overview', label: 'Overview', icon: 'bi-clipboard-data' },
      { key: 'license', label: 'License', icon: 'bi-key' },
      { key: 'settings', label: 'Org settings', icon: 'bi-sliders' }
    ]
  },
  {
    group: 'People',
    items: [
      { key: 'users', label: 'Users', icon: 'bi-people' },
      { key: 'roles', label: 'Roles', icon: 'bi-person-badge' }
    ]
  },
  {
    group: 'Locations',
    items: [
      { key: 'retail-stores', label: 'Retail Stores', icon: 'bi-shop' },
      { key: 'warehouses', label: 'Warehouses', icon: 'bi-boxes' },
      { key: 'production-units', label: 'Production Units', icon: 'bi-nut' }
    ]
  },
  {
    group: 'Catalog',
    items: [
      { key: 'organization-materials', label: 'Materials', icon: 'bi-box-seam' },
      { key: 'organization-services', label: 'Services', icon: 'bi-tools' },
      { key: 'quality-grades', label: 'Quality Grades', icon: 'bi-award' },
      { key: 'products', label: 'Products', icon: 'bi-tags' }
    ]
  },
  {
    group: 'Sell online',
    items: [
      { key: 'online-store', label: 'Online Store', icon: 'bi-bag-heart' }
    ]
  },
  {
    group: 'Data',
    items: [
      { key: 'import-export', label: 'Import / Export', icon: 'bi-arrow-down-up' },
      { key: 'edit-log', label: 'Edit Log', icon: 'bi-clock-history' }
    ]
  }
];

const ORG_ADMIN_SECTION_KEYS = ORG_ADMIN_SECTIONS.flatMap((s) => s.items.map((i) => i.key));

function OrgAdmin() {
  const { user, refreshDateFormat, storeEnabled } = useAuth();
  const [loading, setLoading] = useState(true);

  // The open section is in the address (/org-admin/<section>)
  const [activeTab, setActiveTab] = useSectionTab('/org-admin', ORG_ADMIN_SECTION_KEYS);

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalRoles: 0,
    activeUsers: 0
  });
  const [subscription, setSubscription] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('MONTHLY');
  const [planRequestSubmitting, setPlanRequestSubmitting] = useState(false);
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const [users, setUsers] = useState([]);
  const [twoFactorOn, setTwoFactorOn] = useState([]);
  const [roles, setRoles] = useState([]);
  
  // User Management state
  const [showUserModal, setShowUserModal] = useState(false);
  const [showRoleAssignmentModal, setShowRoleAssignmentModal] = useState(false);
  const [selectedUserForRoleAssignment, setSelectedUserForRoleAssignment] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  // Create user: what the email is (null: not checked yet, 'checking', or { status: NEW | EXISTS | MEMBER, name })
  const [emailStatus, setEmailStatus] = useState(null);
  const [passwordChoice, setPasswordChoice] = useState('EMAIL');
  const [sendingLink, setSendingLink] = useState(false);
  const [userFormData, setUserFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    password: ''
  });
  
  // Role Management state
  const [permissions, setPermissions] = useState([]);
  const [permissionsGrouped, setPermissionsGrouped] = useState({});
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleFormData, setRoleFormData] = useState({
    name: '',
    description: '',
    isActive: true
  });
  const [rolePermissions, setRolePermissions] = useState([]);
  
  // Warehouse state
  const [warehouses, setWarehouses] = useState([]);
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [warehouseFormData, setWarehouseFormData] = useState({
    name: '',
    description: '',
    address: '',
    city: '',
    district: '',
    state: '',
    postalCode: '',
    country: '',
    phoneNumber: '',
    email: '',
    isActive: true
  });
  const [selectedWarehouseCountryCode, setSelectedWarehouseCountryCode] = useState('');

  // Retail Store state
  const [retailStores, setRetailStores] = useState([]);
  const [showRetailStoreModal, setShowRetailStoreModal] = useState(false);
  const [selectedRetailStore, setSelectedRetailStore] = useState(null);
  const [retailStoreFormData, setRetailStoreFormData] = useState({
    name: '',
    description: '',
    address: '',
    city: '',
    district: '',
    state: '',
    postalCode: '',
    country: '',
    phoneNumber: '',
    email: '',
    isActive: true
  });
  const [selectedRetailStoreCountryCode, setSelectedRetailStoreCountryCode] = useState('');

  // Production Unit state
  const [productionUnits, setProductionUnits] = useState([]);
  const [showProductionUnitModal, setShowProductionUnitModal] = useState(false);
  const [selectedProductionUnit, setSelectedProductionUnit] = useState(null);
  const [productionUnitFormData, setProductionUnitFormData] = useState({
    name: '',
    description: '',
    address: '',
    city: '',
    district: '',
    state: '',
    postalCode: '',
    country: '',
    phoneNumber: '',
    email: '',
    isActive: true
  });
  const [selectedProductionUnitCountryCode, setSelectedProductionUnitCountryCode] = useState('');

  // Organization settings state
  const [organizationData, setOrganizationData] = useState(null);
  // Bank accounts that can be printed on invoices (Org settings)
  const [invoiceBankAccounts, setInvoiceBankAccounts] = useState([]);
  const [organizationFormData, setOrganizationFormData] = useState({
    name: '',
    email: '',
    description: '',
    phoneNumber: '',
    address: '',
    city: '',
    district: '',
    state: '',
    country: '',
    postalCode: '',
    website: '',
    defaultCurrency: '',
    dateFormat: '',
    logoUrl: '',
    gstin: '',
    pan: '',
    legalName: '',
    tradeName: '',
    gstRegistrationType: '',
    tdsEnabled: false,
    expiryEnabled: false,
    priceListsEnabled: false,
    twoFactorRequired: false,
    tan: '',
    upiId: '',
    invoiceTerms: '',
    purchaseOrderTerms: '',
    invoiceBankAccountId: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState('');
  
  // Organization Materials state
  const [organizationMaterials, setOrganizationMaterials] = useState([]);
  const [items, setItems] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [materialCatalogDetails, setMaterialCatalogDetails] = useState(null);
  const [loadingCatalogDetails, setLoadingCatalogDetails] = useState(false);
  const [editingItemDetails, setEditingItemDetails] = useState(false);
  const [itemDetailsForm, setItemDetailsForm] = useState({ name: '', itemTypeId: '', hsCode: '', gtin: '', description: '', genericItemId: '', attributes: {} });
  const [savingItemDetails, setSavingItemDetails] = useState(false);
  const [materialFormData, setMaterialFormData] = useState({
    itemId: '',
    sku: '',
    moq: '',
    ssq: '',
    rop: '',
    poq: '',
    unitOfMeasurementId: '',
    gstRate: ''
  });
  const [itemTypes, setItemTypes] = useState([]);
  const [materialCreateMode, setMaterialCreateMode] = useState('existing'); // 'existing' | 'draft'
  // Opening stock typed with a material (booked through the opening balances), and "Save and add another" for loading many
  const [openingStock, setOpeningStock] = useState(emptyOpeningStock());
  const [openingInfo, setOpeningInfo] = useOpeningStockInfo(showMaterialModal);
  const addAnotherRef = useRef(false);
  const [catalogReloadKey, setCatalogReloadKey] = useState(0); // changes when a material is added or removed, so the Global Material Master list refreshes
  const materialFormRef = useRef(null);
  const [draftAttributes, setDraftAttributes] = useState({});
  // Set when the form was started from a Global Material Master item ("Make a variant…"): the item it is a variant of
  const [variantOf, setVariantOf] = useState(null);
  // A category this organization made (not a shared one): its attributes can be managed from the item form
  const isOwnCategory = (id) => !!itemTypes.find((t) => String(t.id) === String(id))?.organization;
  const [draftMaterialForm, setDraftMaterialForm] = useState({ itemName: '', itemTypeId: '', itemDescription: '', itemSku: '', hsCode: '', gtin: '', genericItemId: '' });
  const [savingDraftMaterial, setSavingDraftMaterial] = useState(false);

  // Organization Services state
  const [organizationServices, setOrganizationServices] = useState([]);
  const [services, setServices] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [serviceCreateMode, setServiceCreateMode] = useState('existing'); // 'existing' | 'draft'
  const [draftServiceForm, setDraftServiceForm] = useState({ serviceName: '', serviceTypeId: '', serviceDescription: '' });
  const [savingDraftService, setSavingDraftService] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceFormData, setServiceFormData] = useState({
    serviceId: '',
    hourlyRate: '',
    dailyRate: '',
    fixedPrice: '',
    pricingModel: '',
    estimatedDurationHours: '',
    unitOfMeasurement: '',
    notes: ''
  });
  
  // Products state
  const [products, setProducts] = useState([]);
  const [materialMasterItems, setMaterialMasterItems] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productFormData, setProductFormData] = useState({
    itemId: '',
    name: '',
    sku: '',
    description: '',
    price: '',
    costPrice: '',
    category: '',
    brand: '',
    videoUrl: ''
  });
  const [productCreateMode, setProductCreateMode] = useState('existing'); // 'existing' | 'draft'
  const [draftProductItemForm, setDraftProductItemForm] = useState({ itemName: '', itemTypeId: '', itemDescription: '', itemSku: '', hsCode: '', gtin: '', genericItemId: '' });
  const [savingDraftProduct, setSavingDraftProduct] = useState(false);
  // A new product can come with its opening stock (the same booking as a material's), and "Save and add another" loads many
  const [productOpeningStock, setProductOpeningStock] = useState(emptyOpeningStock());
  const [productOpeningInfo, setProductOpeningInfo] = useOpeningStockInfo(showProductModal);
  const [productUnitId, setProductUnitId] = useState('');
  const [pickedProductItem, setPickedProductItem] = useState(null); // the Global Material Master row a new product was started from
  const [productCatalogReloadKey, setProductCatalogReloadKey] = useState(0);
  const productFormRef = useRef(null);
  const productAnotherRef = useRef(false);

  // Help pane state

  // Print view state
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [printType, setPrintType] = useState(null);

  // Common currency codes
  const currencyCodes = [
    { code: 'USD', name: 'US Dollar' },
    { code: 'EUR', name: 'Euro' },
    { code: 'GBP', name: 'British Pound' },
    { code: 'INR', name: 'Indian Rupee' },
    { code: 'JPY', name: 'Japanese Yen' },
    { code: 'CNY', name: 'Chinese Yuan' },
    { code: 'AUD', name: 'Australian Dollar' },
    { code: 'CAD', name: 'Canadian Dollar' },
    { code: 'CHF', name: 'Swiss Franc' },
    { code: 'SGD', name: 'Singapore Dollar' },
    { code: 'AED', name: 'UAE Dirham' },
    { code: 'SAR', name: 'Saudi Riyal' },
    { code: 'PKR', name: 'Pakistani Rupee' },
    { code: 'BDT', name: 'Bangladeshi Taka' },
    { code: 'LKR', name: 'Sri Lankan Rupee' }
  ];

  useEffect(() => {
    if (activeTab === 'overview' || activeTab === 'license') {
      fetchOverview();
    } else if (activeTab === 'users') {
      fetchUsers();
      fetchRoles(); // Fetch roles when Users tab is active for role assignment
    } else if (activeTab === 'roles') {
      fetchRoles();
      fetchPermissions();
    } else if (activeTab === 'warehouses') {
      fetchWarehouses();
    } else if (activeTab === 'retail-stores') {
      fetchRetailStores();
    } else if (activeTab === 'production-units') {
      fetchProductionUnits();
    } else if (activeTab === 'organization-materials') {
      fetchOrganizationMaterials();
      fetchItems();
      fetchUOMs();
      fetchItemTypes();
    } else if (activeTab === 'organization-services') {
      fetchOrganizationServices();
      fetchServices();
      fetchOrganizationSettings({ silent: true });
      fetchServiceTypes();
    } else if (activeTab === 'products') {
      fetchProducts();
      fetchMaterialMasterItems();
      fetchOrganizationSettings();
      fetchItemTypes();
    } else if (activeTab === 'quality-grades') {
      fetchOrganizationSettings({ silent: true });
    } else if (activeTab === 'settings') {
      fetchOrganizationSettings();
    }
  }, [activeTab]);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const [usersRes, rolesRes] = await Promise.all([
        axios.get('/api/users'),
        axios.get('/api/roles')
      ]);

      const allUsers = usersRes.data;
      const allRoles = rolesRes.data;

      setStats({
        totalUsers: allUsers.length,
        totalRoles: allRoles.length,
        activeUsers: allUsers.filter(u => u.isActive).length
      });
    } catch (error) {
      toast.error('Error fetching overview data');
      console.error(error);
    } finally {
      setLoading(false);
    }
    axios.get('/api/organizations/subscription').then((r) => setSubscription(r.data)).catch(() => {});
    axios.get('/api/organizations/subscription/plans').then((r) => setAvailablePlans(r.data)).catch(() => {});
  };

  const handleSubmitPlanRequest = async () => {
    if (!selectedPlanId) {
      toast.error('Please choose a plan');
      return;
    }
    setPlanRequestSubmitting(true);
    try {
      await axios.post('/api/organizations/subscription/requests', {
        planId: selectedPlanId,
        billingCycle: selectedBillingCycle
      });
      toast.success('Subscription request submitted');
      setSelectedPlanId('');
      fetchOverview();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error submitting request';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error submitting request');
    } finally {
      setPlanRequestSubmitting(false);
    }
  };

  const handleCancelPlanRequest = async (requestId) => {
    try {
      await axios.delete(`/api/organizations/subscription/requests/${requestId}`);
      toast.success('Request cancelled');
      fetchOverview();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error cancelling request';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error cancelling request');
    }
  };

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  const handlePayNow = async (requestId) => {
    setPaymentInProgress(true);
    try {
      const { data: order } = await axios.post(`/api/organizations/subscription/requests/${requestId}/pay`);

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded || !order.keyId) {
        toast.error('Online payment is not available right now. Please contact your administrator.');
        return;
      }

      const razorpay = new window.Razorpay({
        key: order.keyId,
        amount: Math.round(order.amount * 100),
        currency: order.currency,
        order_id: order.gatewayOrderId,
        name: 'iBigDo',
        description: 'Subscription plan payment',
        handler: async (response) => {
          try {
            await axios.post(`/api/organizations/subscription/payments/${order.paymentId}/verify`, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature
            });
            toast.success('Payment successful - your plan is now active');
            fetchOverview();
          } catch (error) {
            const errorMsg = error.response?.data || 'Payment verification failed';
            toast.error(typeof errorMsg === 'string' ? errorMsg : 'Payment verification failed');
          }
        },
        modal: {
          ondismiss: () => setPaymentInProgress(false)
        }
      });
      razorpay.open();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error starting payment';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error starting payment');
    } finally {
      setPaymentInProgress(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users');
      setUsers(response.data);
      try {
        // Who has two-step sign-in on (Org Admin only; a failure just leaves the badges off)
        const two = await axios.get('/api/two-factor/users');
        setTwoFactorOn(two.data?.enabledUserIds || []);
      } catch (e) {
        setTwoFactorOn([]);
      }
    } catch (error) {
      toast.error('Error fetching users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const checkEmail = async (email) => {
    const value = (email || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) { setEmailStatus(null); return; }
    setEmailStatus('checking');
    try {
      const { data } = await axios.get('/api/users/email-status', { params: { email: value } });
      setEmailStatus(data);
    } catch (error) {
      setEmailStatus(null); // the server checks again when saving
    }
  };

  const resetTwoFactor = async () => {
    if (!editingUser) return;
    if (!window.confirm(`Reset two-step sign-in for ${editingUser.email}? Only do this once you are sure it is them. They will set it up again at their next sign-in if you require it.`)) return;
    try {
      const { data } = await axios.post(`/api/two-factor/users/${editingUser.id}/reset`);
      toast.success(data?.message || 'Reset');
      fetchUsers();
    } catch (error) {
      toast.error(typeof error.response?.data === 'string' ? error.response.data : 'Could not reset it');
    }
  };

  const sendSetPasswordLink = async () => {
    if (!editingUser) return;
    setSendingLink(true);
    try {
      const { data } = await axios.post(`/api/users/${editingUser.id}/send-set-password-link`);
      toast.success(data?.message || 'Link sent');
    } catch (error) {
      toast.error(typeof error.response?.data === 'string' ? error.response.data : 'Could not send the link');
    } finally {
      setSendingLink(false);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setEmailStatus(null);
    setPasswordChoice('EMAIL');
    setUserFormData({
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      password: ''
    });
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserFormData({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phoneNumber: user.phoneNumber || '',
      password: ''
    });
    setShowUserModal(true);
  };

  const handleDeleteUser = async (person) => {
    const who = `${person.firstName || ''} ${person.lastName || ''}`.trim() || person.email;
    if (!window.confirm(`Remove ${who} from your organization? They will no longer be able to sign in to it. Their past entries stay as they are.`)) {
      return;
    }
    try {
      await axios.delete(`/api/users/${person.id}`);
      toast.success(`${who} was removed`);
      fetchUsers();
    } catch (error) {
      // The server says why (for example, an organization must keep one Org Admin)
      toast.error(typeof error.response?.data === 'string' && error.response.data ? error.response.data : 'Could not remove the user');
    }
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (userFormData.phoneNumber && !isValidPhoneNumber(userFormData.phoneNumber)) {
      toast.error('Phone number doesn\'t look valid');
      return;
    }
    if (!editingUser && passwordChoice === 'SET' && emailStatus?.status !== 'EXISTS') {
      const weak = passwordProblem(userFormData.password);
      if (weak) {
        toast.error(weak);
        return;
      }
    }
    try {
      if (editingUser) {
        // Don't send password when editing
        const { password, ...updateData } = userFormData;
        await axios.put(`/api/users/${editingUser.id}`, updateData);
        toast.success('User updated successfully');
      } else {
        // Someone with a login is added as they are; a new person gets the typed password or an emailed link
        const body = emailStatus?.status === 'EXISTS'
          ? { email: userFormData.email }
          : { ...userFormData, password: passwordChoice === 'SET' ? userFormData.password : '' };
        const response = await axios.post('/api/users', body);
        if (response.data?.emailSent === false) {
          toast.warn(response.data.message);
        } else {
          toast.success(response.data?.message || 'User created');
        }
      }
      setShowUserModal(false);
      setEditingUser(null);
      setUserFormData({
        email: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        password: ''
      });
      fetchUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data || 'Error saving user';
      toast.error(errorMessage);
    }
  };

  const handleCloseUserModal = () => {
    setShowUserModal(false);
    setEditingUser(null);
    setUserFormData({
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      password: ''
    });
  };

  const handleViewPrint = async (data, type) => {
    setPrintType(type);
    if (type === 'MATERIAL' && data?.item?.id) {
      setPrintData({ ...data, catalogDetails: null, catalogDetailsLoading: true });
      setShowPrintModal(true);
      try {
        const response = await axios.get(`/api/inventory/items/${data.item.id}/catalog-details`);
        setPrintData({ ...data, catalogDetails: response.data || null, catalogDetailsLoading: false });
      } catch (error) {
        console.error(error);
        setPrintData({ ...data, catalogDetails: null, catalogDetailsLoading: false });
      }
      return;
    }
    setPrintData(data);
    setShowPrintModal(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleManageUserRoles = async (user) => {
    // Fetch the latest user data with roles
    try {
      const response = await axios.get(`/api/users/${user.id}`);
      setSelectedUserForRoleAssignment(response.data);
      setShowRoleAssignmentModal(true);
    } catch (error) {
      toast.error('Error loading user data');
      console.error(error);
    }
  };

  const handleCloseRoleAssignmentModal = () => {
    setShowRoleAssignmentModal(false);
    setSelectedUserForRoleAssignment(null);
  };

  const handleAssignRoleToUser = async (roleId) => {
    if (!selectedUserForRoleAssignment) return;
    
    try {
      await axios.put(`/api/users/${selectedUserForRoleAssignment.id}/assign-role/${roleId}`);
      toast.success('Role assigned successfully');
      // Refresh users to get updated role list
      fetchUsers();
      // Refresh the selected user
      const response = await axios.get(`/api/users/${selectedUserForRoleAssignment.id}`);
      setSelectedUserForRoleAssignment(response.data);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data || 'Error assigning role';
      toast.error(errorMsg);
    }
  };

  // Org Admin can't be taken from yourself, or from the organization's only active Org Admin (the server refuses both)
  const orgAdminRemovalBlock = (target) => {
    if (!target) return null;
    if ((user?.id && target.id === user.id) || (user?.email && target.email === user.email)) {
      return 'You can\'t remove Org Admin from yourself. Another Org Admin of this organization can.';
    }
    const activeAdmins = users.filter((u) => u.isActive !== false && (u.roles || []).some(isOrgAdminRole)).length;
    return activeAdmins <= 1 ? 'This is the organization\'s only Org Admin. Make someone else Org Admin first.' : null;
  };

  const handleRemoveRoleFromUser = async (roleId) => {
    if (!selectedUserForRoleAssignment) return;
    
    if (!window.confirm('Are you sure you want to remove this role from the user?')) {
      return;
    }
    
    try {
      await axios.delete(`/api/users/${selectedUserForRoleAssignment.id}/remove-role/${roleId}`);
      toast.success('Role removed successfully');
      // Refresh users to get updated role list
      fetchUsers();
      // Refresh the selected user
      const response = await axios.get(`/api/users/${selectedUserForRoleAssignment.id}`);
      setSelectedUserForRoleAssignment(response.data);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data || 'Error removing role';
      toast.error(errorMsg);
    }
  };

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/roles');
      setRoles(response.data);
    } catch (error) {
      toast.error('Error fetching roles');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get('/api/permissions/grouped');
      setPermissionsGrouped(response.data);
      
      // Flatten for easier access
      const allPermissions = Object.values(response.data).flat();
      setPermissions(allPermissions);
    } catch (error) {
      toast.error('Error fetching permissions');
      console.error(error);
    }
  };

  const fetchRolePermissions = async (roleId) => {
    try {
      const response = await axios.get(`/api/roles/${roleId}/permissions`);
      setRolePermissions(response.data.map(p => p.id));
    } catch (error) {
      toast.error('Error fetching role permissions');
      console.error(error);
    }
  };

  const handleCreateRole = () => {
    setRoleFormData({ name: '', description: '', isActive: true });
    setSelectedRole(null);
    setShowRoleModal(true);
  };

  const handleEditRole = (role) => {
    setRoleFormData({
      name: role.name,
      description: role.description || '',
      isActive: role.isActive
    });
    setSelectedRole(role);
    setShowRoleModal(true);
  };

  const handleManagePermissions = async (role) => {
    setSelectedRole(role);
    await fetchRolePermissions(role.id);
    setShowPermissionModal(true);
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    try {
      if (selectedRole) {
        await axios.put(`/api/roles/${selectedRole.id}`, roleFormData);
        toast.success('Role updated successfully');
      } else {
        await axios.post('/api/roles', roleFormData);
        toast.success('Role created successfully');
      }
      setShowRoleModal(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data || 'Error saving role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/api/roles/${roleId}`);
      toast.success('Role deleted successfully');
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data || 'Error deleting role');
    }
  };

  const handleSavePermissions = async () => {
    try {
      await axios.put(`/api/roles/${selectedRole.id}/permissions`, rolePermissions);
      toast.success('Permissions updated successfully');
      setShowPermissionModal(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data || 'Error updating permissions');
    }
  };

  const togglePermission = (permissionId) => {
    setRolePermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const toggleResourcePermissions = (resource) => {
    const resourcePermissions = permissionsGrouped[resource] || [];
    const resourceIds = resourcePermissions.map(p => p.id);
    const allSelected = resourceIds.every(id => rolePermissions.includes(id));
    
    if (allSelected) {
      // Deselect all
      setRolePermissions(prev => prev.filter(id => !resourceIds.includes(id)));
    } else {
      // Select all
      setRolePermissions(prev => {
        const newIds = resourceIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }
  };

  const fetchWarehouses = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/warehouses');
      setWarehouses(response.data);
    } catch (error) {
      toast.error('Error fetching warehouses');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWarehouse = async () => {
    const defaults = organizationFormData.country || organizationFormData.state
      ? getOrgAddressDefaults(organizationFormData)
      : await loadOrgAddressDefaults(user?.organizationId);
    setWarehouseFormData({
      name: '',
      description: '',
      address: '',
      city: '',
      district: '',
      state: defaults.state,
      postalCode: '',
      country: defaults.country,
      phoneNumber: '',
      email: '',
      isActive: true
    });
    setSelectedWarehouseCountryCode(defaults.countryCode);
    setSelectedWarehouse(null);
    setShowWarehouseModal(true);
  };

  const handleEditWarehouse = (warehouse) => {
    // Find country code from country name
    const countryName = warehouse.country || '';
    const countryObj = countries.find(c => c.name === countryName);
    const countryCode = countryObj ? countryObj.code : '';
    
    setSelectedWarehouseCountryCode(countryCode);
    setWarehouseFormData({
      name: warehouse.name,
      description: warehouse.description || '',
      address: warehouse.address || '',
      city: warehouse.city || '',
      district: warehouse.district || '',
      state: warehouse.state || '',
      postalCode: warehouse.postalCode || '',
      country: countryName,
      phoneNumber: warehouse.phoneNumber || '',
      email: warehouse.email || '',
      isActive: warehouse.isActive
    });
    setSelectedWarehouse(warehouse);
    setShowWarehouseModal(true);
  };

  const handleDeleteWarehouse = async (id) => {
    if (!window.confirm('Are you sure you want to delete this warehouse?')) {
      return;
    }
    try {
      await axios.delete(`/api/inventory/warehouses/${id}`);
      toast.success('Warehouse deleted successfully');
      fetchWarehouses();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting warehouse';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting warehouse');
    }
  };

  const handleSaveWarehouse = async (e) => {
    e.preventDefault();
    try {
      if (selectedWarehouse) {
        await axios.put(`/api/inventory/warehouses/${selectedWarehouse.id}`, warehouseFormData);
        toast.success('Warehouse updated successfully');
      } else {
        await axios.post('/api/inventory/warehouses', warehouseFormData);
        toast.success('Warehouse created successfully');
      }
      setShowWarehouseModal(false);
      fetchWarehouses();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving warehouse';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving warehouse');
    }
  };

  const fetchRetailStores = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/retail-stores');
      setRetailStores(response.data);
    } catch (error) {
      toast.error('Error fetching retail stores');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRetailStore = async () => {
    const defaults = organizationFormData.country || organizationFormData.state
      ? getOrgAddressDefaults(organizationFormData)
      : await loadOrgAddressDefaults(user?.organizationId);
    setRetailStoreFormData({
      name: '',
      description: '',
      address: '',
      city: '',
      district: '',
      state: defaults.state,
      postalCode: '',
      country: defaults.country,
      phoneNumber: '',
      email: '',
      isActive: true
    });
    setSelectedRetailStoreCountryCode(defaults.countryCode);
    setSelectedRetailStore(null);
    setShowRetailStoreModal(true);
  };

  const handleEditRetailStore = (retailStore) => {
    const countryName = retailStore.country || '';
    const countryObj = countries.find(c => c.name === countryName);
    const countryCode = countryObj ? countryObj.code : '';
    setSelectedRetailStoreCountryCode(countryCode);
    setRetailStoreFormData({
      name: retailStore.name,
      description: retailStore.description || '',
      address: retailStore.address || '',
      city: retailStore.city || '',
      district: retailStore.district || '',
      state: retailStore.state || '',
      postalCode: retailStore.postalCode || '',
      country: countryName,
      phoneNumber: retailStore.phoneNumber || '',
      email: retailStore.email || '',
      isActive: retailStore.isActive
    });
    setSelectedRetailStore(retailStore);
    setShowRetailStoreModal(true);
  };

  const handleDeleteRetailStore = async (id) => {
    if (!window.confirm('Are you sure you want to delete this retail store?')) {
      return;
    }
    try {
      await axios.delete(`/api/inventory/retail-stores/${id}`);
      toast.success('Retail store deleted successfully');
      fetchRetailStores();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting retail store';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting retail store');
    }
  };

  const handleSaveRetailStore = async (e) => {
    e.preventDefault();
    try {
      if (selectedRetailStore) {
        await axios.put(`/api/inventory/retail-stores/${selectedRetailStore.id}`, retailStoreFormData);
        toast.success('Retail store updated successfully');
      } else {
        await axios.post('/api/inventory/retail-stores', retailStoreFormData);
        toast.success('Retail store created successfully');
      }
      setShowRetailStoreModal(false);
      fetchRetailStores();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving retail store';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving retail store');
    }
  };

  const fetchProductionUnits = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/production-units');
      setProductionUnits(response.data);
    } catch (error) {
      toast.error('Error fetching production units');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProductionUnit = async () => {
    const defaults = organizationFormData.country || organizationFormData.state
      ? getOrgAddressDefaults(organizationFormData)
      : await loadOrgAddressDefaults(user?.organizationId);
    setProductionUnitFormData({
      name: '',
      description: '',
      address: '',
      city: '',
      district: '',
      state: defaults.state,
      postalCode: '',
      country: defaults.country,
      phoneNumber: '',
      email: '',
      isActive: true
    });
    setSelectedProductionUnitCountryCode(defaults.countryCode);
    setSelectedProductionUnit(null);
    setShowProductionUnitModal(true);
  };

  const handleEditProductionUnit = (productionUnit) => {
    // Find country code from country name
    const countryName = productionUnit.country || '';
    const countryObj = countries.find(c => c.name === countryName);
    const countryCode = countryObj ? countryObj.code : '';
    
    setSelectedProductionUnitCountryCode(countryCode);
    setProductionUnitFormData({
      name: productionUnit.name,
      description: productionUnit.description || '',
      address: productionUnit.address || '',
      city: productionUnit.city || '',
      district: productionUnit.district || '',
      state: productionUnit.state || '',
      postalCode: productionUnit.postalCode || '',
      country: countryName,
      phoneNumber: productionUnit.phoneNumber || '',
      email: productionUnit.email || '',
      isActive: productionUnit.isActive
    });
    setSelectedProductionUnit(productionUnit);
    setShowProductionUnitModal(true);
  };

  const handleDeleteProductionUnit = async (id) => {
    if (!window.confirm('Are you sure you want to delete this production unit?')) {
      return;
    }
    try {
      await axios.delete(`/api/inventory/production-units/${id}`);
      toast.success('Production unit deleted successfully');
      fetchProductionUnits();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting production unit';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting production unit');
    }
  };

  const handleSaveProductionUnit = async (e) => {
    e.preventDefault();
    try {
      if (selectedProductionUnit) {
        await axios.put(`/api/inventory/production-units/${selectedProductionUnit.id}`, productionUnitFormData);
        toast.success('Production unit updated successfully');
      } else {
        await axios.post('/api/inventory/production-units', productionUnitFormData);
        toast.success('Production unit created successfully');
      }
      setShowProductionUnitModal(false);
      fetchProductionUnits();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving production unit';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving production unit');
    }
  };

  const fetchOrganizationMaterials = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/organization-materials');
      setOrganizationMaterials(response.data);
    } catch (error) {
      toast.error('Error fetching organization materials');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await axios.get('/api/inventory/items');
      setItems(response.data);
    } catch (error) {
      toast.error('Error fetching items');
      console.error(error);
    }
  };

  const fetchItemTypes = async () => {
    try {
      const response = await axios.get('/api/organization-item-types');
      setItemTypes(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchUOMs = async () => {
    try {
      const response = await axios.get('/api/inventory/units-of-measurement');
      setUoms(response.data);
    } catch (error) {
      toast.error('Error fetching units of measurement');
      console.error(error);
    }
  };

  // Products functions
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/products');
      setProducts(response.data);
    } catch (error) {
      toast.error('Error fetching products');
    } finally {
      setLoading(false);
    }
  };

  const fetchMaterialMasterItems = async () => {
    try {
      const response = await axios.get('/api/inventory/items');
      const items = response.data;
      setMaterialMasterItems(items || []);
      
      if (!items || items.length === 0) {
        toast.warning('No items found in Material Master. Please create items in System Admin -> Material Master first.');
      }
    } catch (error) {
      const errorMsg = error.response?.data || error.message || 'Error fetching items';
      if (error.response?.status === 403) {
        toast.error('Access denied. Please ensure you have Org Admin role.');
      } else {
        toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error fetching items');
      }
    }
  };

  const handleProductInputChange = (e) => {
    setProductFormData({
      ...productFormData,
      [e.target.name]: e.target.value
    });
  };

  const formatProductSalePrice = (amount) => {
    if (amount === null || amount === undefined || amount === '') {
      return '—';
    }
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (Number.isNaN(num)) {
      return '—';
    }
    const currency = organizationFormData.defaultCurrency || '';
    return currency ? `${currency} ${num.toFixed(2)}` : num.toFixed(2);
  };

  /** Org default currency code for rate labels (e.g. INR), empty until settings are loaded. */
  const orgCurrencyCode = organizationFormData.defaultCurrency || '';
  const rateFieldLabel = (base) => (orgCurrencyCode ? `${base} (${orgCurrencyCode})` : base);

  const formatOrgMoney = (amount) => {
    if (amount === null || amount === undefined || amount === '') {
      return '-';
    }
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (Number.isNaN(num)) {
      return '-';
    }
    return orgCurrencyCode ? `${orgCurrencyCode} ${num.toFixed(2)}` : num.toFixed(2);
  };

  // Picking an HSN code fills in the category from its chapter (only when none is chosen yet, so
  // it never overrides the user's own pick). Naming is offered as a suggestion inside the field.
  const categoryFromHsn = (form, hsnResult) => {
    if (form.itemTypeId) return {};
    const type = findItemTypeForHsn(hsnResult.code, itemTypes);
    if (!type) return {};
    toast.info(`Category set to "${type.name}" from the HSN code's chapter - change it if needed`);
    return { itemTypeId: String(type.id) };
  };

  const emptyProductForm = () => ({
    itemId: '', name: '', sku: '', description: '', price: '', costPrice: '', category: '', brand: ''
  });
  const emptyDraftProductItem = () => ({ itemName: '', itemTypeId: '', itemDescription: '', itemSku: '', hsCode: '', gtin: '', genericItemId: '' });

  // Opens the dialog for a product with a new item of its own
  const handleCreateProduct = () => {
    if (itemTypes.length === 0) fetchItemTypes();
    if (uoms.length === 0) fetchUOMs();
    setEditingProduct(null);
    setPickedProductItem(null);
    setProductFormData(emptyProductForm());
    setDraftProductItemForm(emptyDraftProductItem());
    setProductOpeningStock(emptyOpeningStock());
    setProductUnitId('');
    productAnotherRef.current = false;
    setProductCreateMode('draft');
    setShowProductModal(true);
  };

  // "Make a variant…" on a Global Material Master row: the create form started from that item (category, HSN, description and
  // specification copied, filed under it); the person gives it its own, more specific name and changes what differs
  const handleMakeVariant = async (catalogItem) => {
    try {
      const { data } = await axios.get(`/api/inventory/items/${catalogItem.id}/catalog-details`);
      const item = data?.item || {};
      handleCreateMaterial();
      setVariantOf({ id: catalogItem.id, name: item.name || catalogItem.name });
      setDraftMaterialForm({
        itemName: `${item.name || catalogItem.name}, `,
        itemTypeId: item.itemType?.id ? String(item.itemType.id) : String(catalogItem.categoryId || ''),
        itemDescription: item.description || '',
        itemSku: '',
        hsCode: item.hsCode || '',
        gtin: '',
        genericItemId: String(catalogItem.id)
      });
      setDraftAttributes({ ...(item.attributes || {}) });
    } catch (error) {
      toast.error('Could not load that item to start from');
    }
  };

  // "+ Add" on a Global Material Master row: the usual form for the product's own details, with that item chosen
  const handleAddProductFromCatalog = (catalogItem) => {
    if (uoms.length === 0) fetchUOMs();
    setEditingProduct(null);
    setPickedProductItem(catalogItem);
    setProductFormData({ ...emptyProductForm(), itemId: String(catalogItem.id), name: catalogItem.name });
    setDraftProductItemForm(emptyDraftProductItem());
    setProductOpeningStock(emptyOpeningStock());
    setProductUnitId('');
    productAnotherRef.current = false;
    setProductCreateMode('existing');
    setShowProductModal(true);
  };

  // After a product is saved: book its opening stock if asked (the product stays saved if that fails)
  const bookProductOpeningStock = async (itemId) => {
    if (!productOpeningStock.on || !itemId) return;
    try {
      await saveOpeningStock(productOpeningStock, productOpeningInfo, itemId, productUnitId);
      setProductOpeningInfo((i) => ({ ...i, date: i.date || productOpeningStock.date }));
      toast.success('Opening stock recorded');
    } catch (error) {
      const msg = typeof error.response?.data === 'string' ? error.response.data : 'it could not be saved';
      toast.error(`The product was saved, but its opening stock was not: ${msg}. You can add it under Accounts > Opening Balances.`);
    }
  };

  // Ready for the next product when "Save and add another" was used: the place and date of the opening stock stay
  const startNextProduct = () => {
    setProductFormData(emptyProductForm());
    setDraftProductItemForm(emptyDraftProductItem());
    setProductOpeningStock((o) => ({ ...o, quantity: '', unitCost: '', lotNumber: '' }));
  };

  const handleSaveDraftProduct = async (another = false) => {
    if (!draftProductItemForm.itemTypeId) {
      toast.error('Please select a category');
      return;
    }
    setSavingDraftProduct(true);
    try {
      const created = await axios.post('/api/products/draft-item', {
        // One name, description and SKU for the item and the product; the rest of the form belongs to the product
        productName: draftProductItemForm.itemName,
        productDescription: productFormData.description || null,
        itemTypeId: draftProductItemForm.itemTypeId ? parseInt(draftProductItemForm.itemTypeId) : null,
        itemName: draftProductItemForm.itemName,
        itemDescription: productFormData.description || null,
        itemSku: productFormData.sku || null,
        sku: productFormData.sku || null,
        gtin: draftProductItemForm.gtin || null,
        hsCode: draftProductItemForm.hsCode || null,
        price: productFormData.price,
        costPrice: productFormData.costPrice,
        brand: productFormData.brand || null
      });
      toast.success('Item created and ready to use');
      await bookProductOpeningStock(created.data?.item?.id);
      if (another) startNextProduct(); else handleCloseProductModal();
      fetchProducts();
      setProductCatalogReloadKey((k) => k + 1);
    } catch (error) {
      const errorMsg = error.response?.data || 'Error creating draft product';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error creating draft product');
    } finally {
      setSavingDraftProduct(false);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    const another = productAnotherRef.current && !editingProduct && productCreateMode === 'draft';
    productAnotherRef.current = false;
    if (!editingProduct) {
      // The opening stock is checked first, so a product is never saved with stock that then can't be
      const stockProblem = openingStockProblem(productOpeningStock, productOpeningInfo, productUnitId);
      if (stockProblem) {
        toast.error(stockProblem);
        return;
      }
    }
    if (productCreateMode === 'draft') {
      return handleSaveDraftProduct(another);
    }
    try {
      const payload = {
        ...productFormData,
        price:
          productFormData.price !== '' && productFormData.price != null
            ? parseFloat(productFormData.price)
            : null,
        costPrice:
          productFormData.costPrice !== '' && productFormData.costPrice != null
            ? parseFloat(productFormData.costPrice)
            : null,
        item: productFormData.itemId ? { id: parseInt(productFormData.itemId) } : null
      };
      delete payload.itemId;

      if (editingProduct) {
        await axios.put(`/api/products/${editingProduct.id}`, payload);
        toast.success('Product updated successfully');
      } else {
        const created = await axios.post('/api/products', payload);
        toast.success('Product created successfully');
        await bookProductOpeningStock(created.data?.item?.id || (productFormData.itemId ? parseInt(productFormData.itemId) : null));
      }
      handleCloseProductModal();
      fetchProducts();
      setProductCatalogReloadKey((k) => k + 1);
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving product';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving product');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setPickedProductItem(null);
    setProductCreateMode('existing');
    setProductFormData({
      itemId: product.item?.id || '',
      name: product.name,
      sku: product.sku,
      description: product.description,
      price: product.price ?? '',
      costPrice: product.costPrice,
      category: product.category,
      brand: product.brand,
      videoUrl: product.videoUrl || ''
    });
    setShowProductModal(true);
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await axios.delete(`/api/products/${id}`);
        toast.success('Product deleted successfully');
        fetchProducts();
      } catch (error) {
        toast.error('Error deleting product');
      }
    }
  };

  const handleCloseProductModal = () => {
    setShowProductModal(false);
    setEditingProduct(null);
    setProductFormData({
      itemId: '',
      name: '',
      sku: '',
      description: '',
      price: '',
      costPrice: '',
      category: '',
      brand: ''
    });
    setProductCreateMode('existing');
    setDraftProductItemForm({ itemName: '', itemTypeId: '', itemDescription: '', itemSku: '', hsCode: '', gtin: '', genericItemId: '' });
    setPickedProductItem(null);
    setProductOpeningStock(emptyOpeningStock());
    setProductUnitId('');
  };

  const fetchMaterialCatalogDetails = async (itemId) => {
    setEditingItemDetails(false);
    if (!itemId) {
      setMaterialCatalogDetails(null);
      return;
    }
    try {
      setLoadingCatalogDetails(true);
      const response = await axios.get(`/api/inventory/items/${itemId}/catalog-details`);
      setMaterialCatalogDetails(response.data || null);
    } catch (error) {
      console.error(error);
      setMaterialCatalogDetails(null);
      toast.error('Could not load material details from Material Master');
    } finally {
      setLoadingCatalogDetails(false);
    }
  };

  // Only an item this org itself created (via a draft) is editable here - a shared/global
  // Material Master item, or one drafted by a different org, is not.
  const canEditItemDetails = (item) => !!item?.createdByOrganization;

  const handleStartEditItemDetails = () => {
    const item = materialCatalogDetails?.item;
    if (!item) return;
    setItemDetailsForm({
      name: item.name || '',
      itemTypeId: item.itemType?.id ? item.itemType.id.toString() : '',
      hsCode: item.hsCode || '',
      gtin: item.gtin || '',
      description: item.description || '',
      genericItemId: item.genericItemId ? String(item.genericItemId) : '',
      attributes: item.attributes || {}
    });
    setEditingItemDetails(true);
  };

  const handleSaveItemDetails = async () => {
    const item = materialCatalogDetails?.item;
    if (!item) return;
    if (!itemDetailsForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!itemDetailsForm.itemTypeId) {
      toast.error('Please select a category');
      return;
    }
    setSavingItemDetails(true);
    try {
      await axios.put(`/api/organization-materials/items/${item.id}`, {
        name: itemDetailsForm.name,
        itemType: itemDetailsForm.itemTypeId ? { id: parseInt(itemDetailsForm.itemTypeId, 10) } : null,
        hsCode: itemDetailsForm.hsCode || null,
        gtin: itemDetailsForm.gtin || null,
        description: itemDetailsForm.description || null,
        genericItemId: itemDetailsForm.genericItemId ? parseInt(itemDetailsForm.genericItemId, 10) : null,
        attributes: itemDetailsForm.attributes || {}
      });
      toast.success('Item details updated');
      setEditingItemDetails(false);
      fetchMaterialCatalogDetails(item.id);
    } catch (error) {
      const errorMsg = error.response?.data || 'Error updating item details';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error updating item details');
    } finally {
      setSavingItemDetails(false);
    }
  };

  const handleCreateMaterial = () => {
    setMaterialFormData({
      itemId: '',
      sku: '',
      moq: '',
      ssq: '',
      rop: '',
      poq: '',
      unitOfMeasurementId: '',
      gstRate: ''
    });
    setSelectedMaterial(null);
    setMaterialCatalogDetails(null);
    setMaterialCreateMode('draft');
    setVariantOf(null);
    setDraftMaterialForm({ itemName: '', itemTypeId: '', itemDescription: '', itemSku: '', hsCode: '', gtin: '', genericItemId: '' });
    setDraftAttributes({});
    setOpeningStock(emptyOpeningStock());
    addAnotherRef.current = false;
    setShowMaterialModal(true);
  };

  // "+ Add" on a Global Material Master row: the usual form for the organization's own details, with that item chosen
  const handleAddFromCatalog = (catalogItem) => {
    setMaterialFormData({ itemId: String(catalogItem.id), sku: '', moq: '', ssq: '', rop: '', poq: '', unitOfMeasurementId: '', gstRate: '' });
    setSelectedMaterial(null);
    setMaterialCreateMode('existing');
    setDraftMaterialForm({ itemName: '', itemTypeId: '', itemDescription: '', itemSku: '', hsCode: '', gtin: '', genericItemId: '' });
    setDraftAttributes({});
    setOpeningStock(emptyOpeningStock());
    addAnotherRef.current = false;
    setShowMaterialModal(true);
    fetchMaterialCatalogDetails(catalogItem.id);
  };

  const handleEditMaterial = (material) => {
    const itemId = material.item?.id || '';
    setMaterialFormData({
      itemId,
      sku: material.sku || '',
      moq: material.moq || '',
      ssq: material.ssq || '',
      rop: material.rop || '',
      poq: material.poq || '',
      unitOfMeasurementId: material.unitOfMeasurement?.id || '',
      gstRate: material.gstRate != null ? String(Number(material.gstRate)) : ''
    });
    setSelectedMaterial(material);
    setOpeningStock(emptyOpeningStock());
    addAnotherRef.current = false;
    setShowMaterialModal(true);
    if (itemId) {
      fetchMaterialCatalogDetails(itemId);
    } else {
      setMaterialCatalogDetails(null);
    }
  };

  const handleDeleteMaterial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this organization material?')) {
      return;
    }
    try {
      await axios.delete(`/api/organization-materials/${id}`);
      toast.success('Organization material deleted successfully');
      fetchOrganizationMaterials();
      setCatalogReloadKey((k) => k + 1);
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting organization material';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting organization material');
    }
  };

  // After a material is saved: book its opening stock if asked (the material stays saved if that fails), and either close or get ready for the next one
  const bookOpeningStock = async (itemId) => {
    if (!openingStock.on) return;
    try {
      await saveOpeningStock(openingStock, openingInfo, itemId, materialFormData.unitOfMeasurementId);
      setOpeningInfo((i) => ({ ...i, date: i.date || openingStock.date }));
      toast.success('Opening stock recorded');
    } catch (error) {
      const msg = typeof error.response?.data === 'string' ? error.response.data : 'it could not be saved';
      toast.error(`The material was saved, but its opening stock was not: ${msg}. You can add it under Accounts > Opening Balances.`);
    }
  };

  const startNextMaterial = () => {
    setMaterialFormData({ itemId: '', sku: '', moq: '', ssq: '', rop: '', poq: '', unitOfMeasurementId: '', gstRate: '' });
    setSelectedMaterial(null);
    setMaterialCatalogDetails(null);
    setVariantOf(null);
    setDraftMaterialForm({ itemName: '', itemTypeId: '', itemDescription: '', itemSku: '', hsCode: '', gtin: '', genericItemId: '' });
    setDraftAttributes({});
    setOpeningStock((o) => ({ ...o, quantity: '', unitCost: '', lotNumber: '' }));
  };

  const handleSaveDraftMaterial = async (another = false) => {
    if (!draftMaterialForm.itemTypeId) {
      toast.error('Please select a category');
      return;
    }
    if (variantOf && draftMaterialForm.itemName.trim().replace(/[,\s]+$/, '').toLowerCase() === variantOf.name.toLowerCase()) {
      toast.error(`Give it a more specific name than "${variantOf.name}", for example "${variantOf.name}, 50 mm"`);
      return;
    }
    setSavingDraftMaterial(true);
    try {
      const created = await axios.post('/api/organization-materials/draft-item', {
        itemName: draftMaterialForm.itemName,
        itemTypeId: draftMaterialForm.itemTypeId ? parseInt(draftMaterialForm.itemTypeId) : null,
        itemDescription: draftMaterialForm.itemDescription || null,
        itemSku: draftMaterialForm.itemSku || null,
        gtin: draftMaterialForm.gtin || null,
        hsCode: draftMaterialForm.hsCode || null,
        genericItemId: draftMaterialForm.genericItemId ? parseInt(draftMaterialForm.genericItemId, 10) : null,
        attributes: draftAttributes,
        sku: materialFormData.sku || null,
        moq: materialFormData.moq ? parseFloat(materialFormData.moq) : null,
        ssq: materialFormData.ssq ? parseFloat(materialFormData.ssq) : null,
        rop: materialFormData.rop ? parseFloat(materialFormData.rop) : null,
        poq: materialFormData.poq ? parseFloat(materialFormData.poq) : null,
        unitOfMeasurementId: materialFormData.unitOfMeasurementId ? parseInt(materialFormData.unitOfMeasurementId) : null,
        gstRate: materialFormData.gstRate !== '' ? parseFloat(materialFormData.gstRate) : null
      });
      toast.success('Item created and ready to use');
      await bookOpeningStock(created.data?.item?.id);
      if (another) startNextMaterial(); else setShowMaterialModal(false);
      fetchOrganizationMaterials();
      setCatalogReloadKey((k) => k + 1);
    } catch (error) {
      const errorMsg = error.response?.data || 'Error creating draft material';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error creating draft material');
    } finally {
      setSavingDraftMaterial(false);
    }
  };

  const handleSaveMaterial = async (e) => {
    e.preventDefault();
    const another = addAnotherRef.current && !selectedMaterial;
    addAnotherRef.current = false;
    // The opening stock is checked first, so a material is never saved with stock that then can't be
    const stockProblem = openingStockProblem(openingStock, openingInfo, materialFormData.unitOfMeasurementId);
    if (stockProblem) {
      toast.error(stockProblem);
      return;
    }
    if (materialCreateMode === 'draft') {
      return handleSaveDraftMaterial(another);
    }
    if (!materialFormData.itemId) {
      toast.error('Please select an item');
      return;
    }
    try {
      if (!selectedMaterial) {
        const alreadyAdded = organizationMaterials.some(
          (m) => m.item?.id != null && String(m.item.id) === String(materialFormData.itemId)
        );
        if (alreadyAdded) {
          toast.error('This material is already enabled for your organization. Use Edit to update it.');
          return;
        }
      }
      const payload = {
        item: { id: parseInt(materialFormData.itemId) },
        sku: materialFormData.sku || null,
        moq: materialFormData.moq ? parseFloat(materialFormData.moq) : null,
        ssq: materialFormData.ssq ? parseFloat(materialFormData.ssq) : null,
        rop: materialFormData.rop ? parseFloat(materialFormData.rop) : null,
        poq: materialFormData.poq ? parseFloat(materialFormData.poq) : null,
        unitOfMeasurement: materialFormData.unitOfMeasurementId ? { id: parseInt(materialFormData.unitOfMeasurementId) } : null,
        gstRate: materialFormData.gstRate !== '' ? parseFloat(materialFormData.gstRate) : null
      };

      let saved;
      if (selectedMaterial) {
        saved = await axios.put(`/api/organization-materials/${selectedMaterial.id}`, payload);
        toast.success('Organization material updated successfully');
      } else {
        saved = await axios.post('/api/organization-materials', payload);
        toast.success('Organization material created successfully');
      }
      await bookOpeningStock(saved.data?.item?.id ?? selectedMaterial?.item?.id ?? parseInt(materialFormData.itemId));
      if (another) {
        startNextMaterial();
      } else {
        setShowMaterialModal(false);
        setSelectedMaterial(null);
        setMaterialCatalogDetails(null);
      }
      fetchOrganizationMaterials();
      setCatalogReloadKey((k) => k + 1);
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving organization material';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving organization material');
    }
  };

  const fetchOrganizationServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/organization-services');
      setOrganizationServices(response.data);
    } catch (error) {
      toast.error('Error fetching organization services');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      // Fetch global services from Services Master (created by System Admin)
      const response = await axios.get('/api/system-admin/services-master/services');
      setServices(response.data);
    } catch (error) {
      toast.error('Error fetching services');
      console.error(error);
    }
  };

  const fetchServiceTypes = async () => {
    try {
      const response = await axios.get('/api/system-admin/services-master/service-types');
      setServiceTypes(response.data);
    } catch (error) {
      console.error('Error fetching service types:', error);
    }
  };

  const handleCreateService = () => {
    setServiceFormData({
      serviceId: '',
      hourlyRate: '',
      dailyRate: '',
      fixedPrice: '',
      pricingModel: '',
      estimatedDurationHours: '',
      unitOfMeasurement: '',
      notes: ''
    });
    setSelectedService(null);
    setServiceCreateMode('existing');
    setDraftServiceForm({ serviceName: '', serviceTypeId: '', serviceDescription: '' });
    setShowServiceModal(true);
    if (services.length === 0) fetchServices();
    if (serviceTypes.length === 0) fetchServiceTypes();
  };

  const handleEditService = (orgService) => {
    setServiceFormData({
      serviceId: orgService.service?.id || '',
      hourlyRate: orgService.hourlyRate || '',
      dailyRate: orgService.dailyRate || '',
      fixedPrice: orgService.fixedPrice || '',
      pricingModel: orgService.pricingModel || '',
      estimatedDurationHours: orgService.estimatedDurationHours || '',
      unitOfMeasurement: orgService.unitOfMeasurement || '',
      notes: orgService.notes || ''
    });
    setSelectedService(orgService);
    setShowServiceModal(true);
    if (services.length === 0) fetchServices();
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Are you sure you want to delete this organization service?')) {
      return;
    }
    try {
      await axios.delete(`/api/organization-services/${id}`);
      toast.success('Organization service deleted successfully');
      fetchOrganizationServices();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting organization service';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting organization service');
    }
  };

  const handleSaveDraftService = async () => {
    setSavingDraftService(true);
    try {
      await axios.post('/api/organization-services/draft-service', {
        serviceName: draftServiceForm.serviceName,
        serviceTypeId: draftServiceForm.serviceTypeId ? parseInt(draftServiceForm.serviceTypeId) : null,
        serviceDescription: draftServiceForm.serviceDescription || null,
        hourlyRate: serviceFormData.hourlyRate ? parseFloat(serviceFormData.hourlyRate) : null,
        dailyRate: serviceFormData.dailyRate ? parseFloat(serviceFormData.dailyRate) : null,
        fixedPrice: serviceFormData.fixedPrice ? parseFloat(serviceFormData.fixedPrice) : null,
        pricingModel: serviceFormData.pricingModel || null,
        estimatedDurationHours: serviceFormData.estimatedDurationHours ? parseFloat(serviceFormData.estimatedDurationHours) : null,
        unitOfMeasurement: serviceFormData.unitOfMeasurement || null,
        notes: serviceFormData.notes || null
      });
      toast.success('Draft service created - a System Admin will review it soon');
      setShowServiceModal(false);
      fetchOrganizationServices();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error creating draft service';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error creating draft service');
    } finally {
      setSavingDraftService(false);
    }
  };

  const handleSaveService = async (e) => {
    e.preventDefault();
    if (serviceCreateMode === 'draft') {
      return handleSaveDraftService();
    }
    try {
      if (!selectedService) {
        const alreadyAdded = organizationServices.some(
          (os) => os.service?.id != null && String(os.service.id) === String(serviceFormData.serviceId)
        );
        if (alreadyAdded) {
          toast.error('This service is already enabled for your organization. Use Edit to update it.');
          return;
        }
      }

      const payload = {
        service: { id: parseInt(serviceFormData.serviceId) },
        hourlyRate: serviceFormData.hourlyRate ? parseFloat(serviceFormData.hourlyRate) : null,
        dailyRate: serviceFormData.dailyRate ? parseFloat(serviceFormData.dailyRate) : null,
        fixedPrice: serviceFormData.fixedPrice ? parseFloat(serviceFormData.fixedPrice) : null,
        pricingModel: serviceFormData.pricingModel || null,
        estimatedDurationHours: serviceFormData.estimatedDurationHours ? parseFloat(serviceFormData.estimatedDurationHours) : null,
        unitOfMeasurement: serviceFormData.unitOfMeasurement || null,
        notes: serviceFormData.notes || null
      };

      if (selectedService) {
        await axios.put(`/api/organization-services/${selectedService.id}`, payload);
        toast.success('Organization service updated successfully');
      } else {
        await axios.post('/api/organization-services', payload);
        toast.success('Organization service created successfully');
      }
      setShowServiceModal(false);
      setSelectedService(null);
      fetchOrganizationServices();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving organization service';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving organization service');
    }
  };

  const fetchOrganizationSettings = async (opts = {}) => {
    const silent = opts.silent === true;
    try {
      if (!silent) setLoading(true);
      // Get organization ID from user context
      const response = await axios.get(`/api/organizations/${user?.organizationId}`);
      axios.get('/api/accounts/bank-accounts/active').then((r) => setInvoiceBankAccounts(Array.isArray(r.data) ? r.data : [])).catch(() => setInvoiceBankAccounts([]));
      const org = response.data;
      setOrganizationData(org);
      // Find country code from country name
      const countryName = org.country || '';
      const countryObj = countries.find(c => c.name === countryName);
      const countryCode = countryObj ? countryObj.code : '';
      
      setSelectedCountryCode(countryCode);
      setOrganizationFormData({
        name: org.name || '',
        email: org.email || '',
        description: org.description || '',
        phoneNumber: org.phoneNumber || '',
        address: org.address || '',
        city: org.city || '',
        district: org.district || '',
        state: org.state || '',
        country: org.country || '',
        postalCode: org.postalCode || '',
        website: org.website || '',
        defaultCurrency: org.defaultCurrency || '',
        dateFormat: org.dateFormat || '',
        logoUrl: org.logoUrl || '',
        gstin: org.gstin || '',
        pan: org.pan || '',
        legalName: org.legalName || '',
        tradeName: org.tradeName || '',
        gstRegistrationType: org.gstRegistrationType || '',
        tdsEnabled: Boolean(org.tdsEnabled),
        expiryEnabled: Boolean(org.expiryEnabled),
        priceListsEnabled: Boolean(org.priceListsEnabled),
        twoFactorRequired: Boolean(org.twoFactorRequired),
        tan: org.tan || '',
        upiId: org.upiId || '',
        invoiceTerms: org.invoiceTerms || '',
        purchaseOrderTerms: org.purchaseOrderTerms || '',
        invoiceBankAccountId: org.invoiceBankAccountId ? String(org.invoiceBankAccountId) : ''
      });
    } catch (error) {
      toast.error('Error fetching organization settings');
      console.error(error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Fills the tax profile from a verified GSTIN, without overwriting anything already typed.
  const handleOrgGstinResolved = (found) => {
    setOrganizationFormData((prev) => ({
      ...prev,
      legalName: prev.legalName || found.legalName || '',
      tradeName: prev.tradeName || found.tradeName || '',
      state: prev.state || matchIndianState(found.state) || '',
      gstRegistrationType: prev.gstRegistrationType || 'REGULAR'
    }));
  };

  const handleSaveOrganizationSettings = async (e) => {
    e.preventDefault();
    const website = checkWebAddress('Website', organizationFormData.website);
    const logoUrl = checkWebAddress('Logo URL', organizationFormData.logoUrl);
    const bad = [website, logoUrl].find((c) => c.error);
    if (bad) {
      toast.error(bad.error);
      return;
    }
    try {
      setSavingSettings(true);
      await axios.put('/api/organizations/profile', {
        ...organizationFormData,
        website: website.value,
        logoUrl: logoUrl.value,
        invoiceBankAccountId: organizationFormData.invoiceBankAccountId ? Number(organizationFormData.invoiceBankAccountId) : 0
      });
      toast.success('Organization settings updated successfully');
      // Refresh organization data, and how dates look if that was changed
      await fetchOrganizationSettings();
      refreshDateFormat(user?.organizationId);
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving organization settings';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving organization settings');
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading && (activeTab === 'overview' || activeTab === 'license')) {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4 admin-page-shell">
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <div className="d-flex admin-tab-layout">
          <AdminSectionNav
            sections={ORG_ADMIN_SECTIONS}
            activeKey={activeTab}
            onSelect={setActiveTab}
            ariaLabel="Org Admin sections"
          />
          <div className="flex-grow-1 admin-tab-content-col">
            <Tab.Content>
            {/* Overview Tab */}
            <Tab.Pane eventKey="overview">
              {subscription && subscription.subscriptionStatus && (() => {
                const plan = subscription.subscriptionPlan;
                const status = subscription.subscriptionStatus;
                const subscriptionEndDate = subscription.subscriptionEndDate ? new Date(subscription.subscriptionEndDate) : null;
                const daysLeft = subscriptionEndDate ? Math.ceil((subscriptionEndDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
                const requests = subscription.requests || [];
                const pendingRequest = requests.find((r) => r.status === 'PENDING');

                let variant = 'success';
                let message = plan ? `License: ${plan.name} (${status})` : 'License: No active plan';
                if (!plan) {
                  variant = 'danger';
                  message = 'No active subscription plan - your organization needs a license.';
                } else if (status === 'EXPIRED') {
                  variant = 'danger';
                  message = `Subscription expired${subscriptionEndDate ? ` on ${formatDate(subscriptionEndDate)}` : ''}.`;
                } else if (pendingRequest) {
                  variant = 'info';
                  message = `Subscription request pending: ${pendingRequest.requestedPlan?.name}`;
                } else if (daysLeft != null && daysLeft <= 7) {
                  variant = 'warning';
                  message = `${status === 'TRIAL' ? 'Trial' : 'Plan'} ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'} (${formatDate(subscriptionEndDate)})`;
                }

                return (
                  <Alert variant={variant} className="mt-3 d-flex justify-content-between align-items-center flex-wrap gap-2 mb-0">
                    <span>{message}</span>
                    <Button size="sm" variant={`outline-${variant}`} onClick={() => setActiveTab('license')}>
                      Manage License
                    </Button>
                  </Alert>
                );
              })()}
              <Row className="mt-3">
                <Col md={4}>
                  <Card className="text-center">
                    <Card.Body>
                      <h3>{stats.totalUsers}</h3>
                      <p className="text-muted mb-0">Total Users</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center">
                    <Card.Body>
                      <h3>{stats.activeUsers}</h3>
                      <p className="text-muted mb-0">Active Users</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center">
                    <Card.Body>
                      <h3>{stats.totalRoles}</h3>
                      <p className="text-muted mb-0">Total Roles</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card className="mt-4">
                <Card.Header>
                  <h5 className="mb-0">Quick Actions</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <Card className="mb-3">
                        <Card.Body>
                          <h6>User Management</h6>
                          <p className="text-muted small">Manage users in your organization</p>
                          <Button 
                            variant="primary" 
                            size="sm"
                            onClick={() => setActiveTab('users')}
                          >
                            Go to Users
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={4}>
                      <Card className="mb-3">
                        <Card.Body>
                          <h6>Role Management</h6>
                          <p className="text-muted small">Create and manage roles with permissions</p>
                          <Button 
                            variant="primary" 
                            size="sm"
                            onClick={() => setActiveTab('roles')}
                          >
                            Go to Roles
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={4}>
                      <Card className="mb-3">
                        <Card.Body>
                          <h6>Warehouses</h6>
                          <p className="text-muted small">Manage warehouse locations</p>
                          <Button 
                            variant="primary" 
                            size="sm"
                            onClick={() => setActiveTab('warehouses')}
                          >
                            Go to Warehouses
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={4}>
                      <Card className="mb-3">
                        <Card.Body>
                          <h6>Materials</h6>
                          <p className="text-muted small">Manage organization-specific material information</p>
                          <Button 
                            variant="primary" 
                            size="sm"
                            onClick={() => setActiveTab('organization-materials')}
                          >
                            Go to Materials
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={4}>
                      <Card className="mb-3">
                        <Card.Body>
                          <h6>Products</h6>
                          <p className="text-muted small">Manage products that we manufacture</p>
                          <Button 
                            variant="primary" 
                            size="sm"
                            onClick={() => setActiveTab('products')}
                          >
                            Go to Products
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Alert variant="info" className="mt-4">
                <strong>Organization:</strong> {user?.organizationName}
                <br />
                <strong>Organization ID:</strong> {user?.organizationId}
              </Alert>
            </Tab.Pane>

            {/* License Tab */}
            <Tab.Pane eventKey="license">
              {subscription && subscription.subscriptionStatus && (() => {
                const plan = subscription.subscriptionPlan;
                const usage = subscription.usage || {};
                const status = subscription.subscriptionStatus;
                const subscriptionEndDate = subscription.subscriptionEndDate ? new Date(subscription.subscriptionEndDate) : null;
                const daysLeft = subscriptionEndDate ? Math.ceil((subscriptionEndDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
                const usageLine = (label, count, max) => (
                  max != null ? `${label}: ${count} of ${max} used` : null
                );
                const usageLines = [
                  usageLine('Warehouses', usage.warehouseCount, plan?.maxWarehouses),
                  usageLine('Production units', usage.productionUnitCount, plan?.maxProductionUnits),
                  usageLine('Retail stores', usage.retailStoreCount, plan?.maxRetailStores),
                  usageLine('User licenses', usage.activeUserCount, plan?.maxUsers),
                  usageLine('Documents this month', usage.monthlyDocumentCount, plan?.maxDocumentsPerMonth)
                ].filter(Boolean);
                const planFeatureKeys = new Set(subscription.features || []);
                const featureCatalog = subscription.featureCatalog || [];
                const lackedFeatures = featureCatalog.filter((ft) => !planFeatureKeys.has(ft.key));
                const selectedPlan = availablePlans.find((p) => String(p.id) === String(selectedPlanId));
                const yearlyOnly = Boolean(selectedPlan && Number(selectedPlan.priceMonthly) <= 0 && Number(selectedPlan.priceYearly) > 0);
                const history = subscription.history || [];
                const requests = subscription.requests || [];
                const pendingRequest = requests.find((r) => r.status === 'PENDING');
                return (
                  <>
                  <Card className="mt-3">
                    <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <strong>{plan ? plan.name : 'No active subscription plan'}</strong>
                          <Badge bg={!plan ? 'secondary' : status === 'ACTIVE' ? 'success' : status === 'TRIAL' ? 'info' : status === 'EXPIRED' ? 'danger' : 'secondary'}>
                            {plan ? status : 'No Plan'}
                          </Badge>
                        </div>
                        {plan && status !== 'EXPIRED' && subscriptionEndDate && (
                          <p className="text-muted small mb-1">
                            {daysLeft >= 0
                              ? `${status === 'TRIAL' ? 'Trial' : 'Plan'} ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'} (${formatDate(subscriptionEndDate)})`
                              : `${status === 'TRIAL' ? 'Trial' : 'Plan'} has ended`}
                          </p>
                        )}
                        {plan && status === 'EXPIRED' && (
                          <p className="text-danger small mb-1">
                            Subscription expired{subscriptionEndDate ? ` on ${formatDate(subscriptionEndDate)}` : ''} - contact your administrator to renew or upgrade.
                          </p>
                        )}
                        {!plan && (
                          <p className="text-danger small mb-1">
                            Your organization has no active subscription plan - contact your administrator to set one up.
                          </p>
                        )}
                        {usageLines.length > 0 && (
                          <p className="text-muted small mb-0">{usageLines.join(' · ')}</p>
                        )}
                        {plan && featureCatalog.length > 0 && (
                          <p className="text-muted small mb-0 mt-1">
                            {lackedFeatures.length === 0
                              ? 'Every feature is included in your plan.'
                              : <>Not in your plan: {lackedFeatures.map((ft) => ft.label).join(', ')}. Request a higher plan below to add them.</>}
                          </p>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                  <PlanComparison
                    plans={availablePlans}
                    featureCatalog={featureCatalog}
                    current={plan}
                    canChoose={!pendingRequest}
                    onChoose={(p) => {
                      setSelectedPlanId(String(p.id));
                      if (Number(p.priceMonthly) <= 0 && Number(p.priceYearly) > 0) setSelectedBillingCycle('YEARLY');
                      document.getElementById('plan-request-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                  />
                  <Card className="mt-3" id="plan-request-card">
                    <Card.Header>
                      <h6 className="mb-0">{pendingRequest ? 'Subscription Request' : 'Request a Plan'}</h6>
                    </Card.Header>
                    <Card.Body>
                      {pendingRequest ? (
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                          <div>
                            <p className="mb-1">
                              Request pending: <strong>{pendingRequest.requestedPlan?.name}</strong> ({pendingRequest.billingCycle === 'YEARLY' ? 'Yearly' : 'Monthly'})
                            </p>
                            <p className="text-muted small mb-0">
                              Submitted {pendingRequest.requestedAt ? formatDateTime(pendingRequest.requestedAt) : ''} - waiting for System Admin to review.
                            </p>
                          </div>
                          <div className="d-flex gap-2">
                            <Button variant="primary" size="sm" disabled={paymentInProgress} onClick={() => handlePayNow(pendingRequest.id)}>
                              {paymentInProgress ? 'Starting payment...' : 'Pay Now'}
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleCancelPlanRequest(pendingRequest.id)}>
                              Cancel Request
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-muted small">Choose a plan to request. A System Admin will review and activate it for your organization.</p>
                          <Row className="g-2 align-items-end">
                            <Col md={5}>
                              <Form.Group>
                                <Form.Label className="small mb-1">Plan</Form.Label>
                                <Form.Select size="sm" value={selectedPlanId} onChange={(e) => {
                                  setSelectedPlanId(e.target.value);
                                  const chosen = availablePlans.find((p) => String(p.id) === String(e.target.value));
                                  if (chosen && Number(chosen.priceMonthly) <= 0 && Number(chosen.priceYearly) > 0) setSelectedBillingCycle('YEARLY');
                                }}>
                                  <option value="">-- Choose a plan --</option>
                                  {[...availablePlans].sort((x, y) => Number(x.priceYearly) - Number(y.priceYearly)).map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {p.name}{plan?.id === p.id ? ' (current)' : ''} - {Number(p.priceMonthly) > 0 ? `₹${p.priceMonthly}/mo or ` : ''}₹{p.priceYearly}/yr{Number(p.priceMonthly) > 0 ? '' : ' (yearly only)'}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label className="small mb-1">Billing Cycle</Form.Label>
                                <Form.Select size="sm" value={selectedBillingCycle} onChange={(e) => setSelectedBillingCycle(e.target.value)}>
                                  <option value="MONTHLY" disabled={yearlyOnly}>Monthly</option>
                                  <option value="YEARLY">Yearly</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Button variant="primary" size="sm" className="w-100" disabled={planRequestSubmitting} onClick={handleSubmitPlanRequest}>
                                {planRequestSubmitting ? 'Submitting...' : 'Request Plan'}
                              </Button>
                            </Col>
                          </Row>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                  {history.length > 0 && (
                    <Card className="mt-3">
                      <Card.Header>
                        <h6 className="mb-0">Subscription History</h6>
                      </Card.Header>
                      <Card.Body>
                        <Table size="sm" bordered responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Plan</th>
                              <th>Status</th>
                              <th>Changed By</th>
                            </tr>
                          </thead>
                          <tbody>
                            {history.map((entry) => (
                              <tr key={entry.id}>
                                <td>{entry.changedAt ? formatDateTime(entry.changedAt) : ''}</td>
                                <td>{entry.fromPlan?.name || 'No plan'} &rarr; {entry.toPlan?.name || 'No plan'}</td>
                                <td>{entry.fromStatus || '—'} &rarr; {entry.toStatus || '—'}</td>
                                <td>{entry.changedByName || entry.changedByEmail}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  )}
                  </>
                );
              })()}
              {!subscription && (
                <div className="d-flex justify-content-center mt-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              )}
            </Tab.Pane>

            {/* Settings Tab */}
            <Tab.Pane eventKey="settings">
              <div className="mt-3">
                {loading ? (
                  <div className="d-flex justify-content-center">
                    <Spinner animation="border" role="status" />
                  </div>
                ) : (
                  <Card>
                    <Card.Header>
                      <h5 className="mb-0">Organization Settings</h5>
                      <p className="text-muted mb-0 small">Update your organization information and preferences</p>
                    </Card.Header>
                    <Card.Body>
                      <Form onSubmit={handleSaveOrganizationSettings}>
                        <h6 className="mb-3">Basic Information</h6>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Organization Name *</Form.Label>
                              <Form.Control
                                type="text"
                                value={organizationFormData.name}
                                onChange={(e) => setOrganizationFormData({ ...organizationFormData, name: e.target.value })}
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Email *</Form.Label>
                              <Form.Control
                                type="email"
                                value={organizationFormData.email}
                                onChange={(e) => setOrganizationFormData({ ...organizationFormData, email: e.target.value })}
                                required
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Form.Group className="mb-3">
                          <Form.Label>Description</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={organizationFormData.description}
                            onChange={(e) => setOrganizationFormData({ ...organizationFormData, description: e.target.value })}
                            placeholder="Brief description of your organization"
                          />
                        </Form.Group>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Phone Number</Form.Label>
                              <PhoneNumberInput
                                name="phoneNumber"
                                value={organizationFormData.phoneNumber}
                                onChange={(e) => setOrganizationFormData({ ...organizationFormData, phoneNumber: e.target.value })}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Website</Form.Label>
                              <WebAddressInput
                                https
                                value={organizationFormData.website}
                                onChange={(v) => setOrganizationFormData({ ...organizationFormData, website: v })}
                                placeholder="www.example.com"
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <hr className="my-4" />

                        <h6 className="mb-3">Address Information</h6>
                        <Form.Group className="mb-3">
                          <Form.Label>Address</Form.Label>
                          <Form.Control
                            type="text"
                            value={organizationFormData.address}
                            onChange={(e) => setOrganizationFormData({ ...organizationFormData, address: e.target.value })}
                          />
                        </Form.Group>
                        <Row>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>City</Form.Label>
                              <Form.Control
                                type="text"
                                value={organizationFormData.city}
                                onChange={(e) => setOrganizationFormData({ ...organizationFormData, city: e.target.value })}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>District</Form.Label>
                              <Form.Control
                                type="text"
                                value={organizationFormData.district}
                                onChange={(e) => setOrganizationFormData({ ...organizationFormData, district: e.target.value })}
                                placeholder="District"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>State/Province</Form.Label>
                              {selectedCountryCode && selectedCountryCode !== 'OTHER' && getStatesForCountry(selectedCountryCode).length > 0 ? (
                                <Form.Select
                                  value={organizationFormData.state}
                                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, state: e.target.value })}
                                >
                                  <option value="">Select State/Province</option>
                                  {getStatesForCountry(selectedCountryCode).map(state => (
                                    <option key={state} value={state}>{state}</option>
                                  ))}
                                </Form.Select>
                              ) : (
                                <Form.Control
                                  type="text"
                                  value={organizationFormData.state}
                                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, state: e.target.value })}
                                  placeholder="State/Province"
                                />
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Postal Code</Form.Label>
                              <Form.Control
                                type="text"
                                value={organizationFormData.postalCode}
                                onChange={(e) => setOrganizationFormData({ ...organizationFormData, postalCode: e.target.value })}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Form.Group className="mb-3">
                          <Form.Label>Country</Form.Label>
                          <Form.Select
                            value={selectedCountryCode}
                            onChange={(e) => {
                              const countryCode = e.target.value;
                              const countryObj = countries.find(c => c.code === countryCode);
                              const countryName = countryObj ? countryObj.name : '';
                              setSelectedCountryCode(countryCode);
                              setOrganizationFormData({
                                ...organizationFormData,
                                country: countryName,
                                state: '' // Reset state when country changes
                              });
                            }}
                          >
                            <option value="">Select Country</option>
                            {countries.map(country => (
                              <option key={country.code} value={country.code}>{country.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        {(!selectedCountryCode || selectedCountryCode === 'IN') && (
                          <>
                            <hr className="my-4" />

                            <h6 className="mb-1">GST Tax Profile</h6>
                            <p className="text-muted small mb-3">
                              Shown on your tax invoices as the supplier, and used to decide CGST/SGST versus IGST. Set your
                              State above as well - it's the state your goods are supplied from.
                            </p>
                            <Row>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>GSTIN</Form.Label>
                                  <GstinLookupInput
                                    name="gstin"
                                    value={organizationFormData.gstin}
                                    onChange={(e) => setOrganizationFormData({ ...organizationFormData, gstin: e.target.value.toUpperCase() })}
                                    onResolved={handleOrgGstinResolved}
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>GST registration type</Form.Label>
                                  <Form.Select
                                    value={organizationFormData.gstRegistrationType}
                                    onChange={(e) => setOrganizationFormData({ ...organizationFormData, gstRegistrationType: e.target.value })}
                                  >
                                    <option value="">Not set</option>
                                    <option value="REGULAR">Regular</option>
                                    <option value="COMPOSITION">Composition</option>
                                    <option value="UNREGISTERED">Not registered for GST</option>
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Legal name</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={organizationFormData.legalName}
                                    onChange={(e) => setOrganizationFormData({ ...organizationFormData, legalName: e.target.value })}
                                    placeholder="As registered with GST"
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Trade name</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={organizationFormData.tradeName}
                                    onChange={(e) => setOrganizationFormData({ ...organizationFormData, tradeName: e.target.value })}
                                  />
                                </Form.Group>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>PAN</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={organizationFormData.pan}
                                    maxLength={10}
                                    onChange={(e) => setOrganizationFormData({ ...organizationFormData, pan: e.target.value.toUpperCase() })}
                                    placeholder="Filled from the GSTIN if left blank"
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Expiry dates</Form.Label>
                                  <Form.Check
                                    type="switch"
                                    id="org-expiry-enabled"
                                    label="Our stock has expiry dates"
                                    checked={organizationFormData.expiryEnabled}
                                    onChange={(e) => setOrganizationFormData({ ...organizationFormData, expiryEnabled: e.target.checked })}
                                  />
                                  <Form.Text className="text-muted">
                                    For food, chemicals, medicines and anything else that goes bad. Goods receipts then ask for each lot&apos;s expiry
                                    date, stock ageing shows what is expiring, and the oldest-expiry lot is offered first when you issue.
                                    Most businesses don&apos;t need it.
                                  </Form.Text>
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Price lists</Form.Label>
                                  <Form.Check
                                    type="switch"
                                    id="org-price-lists-enabled"
                                    label="We charge different customers different prices"
                                    checked={organizationFormData.priceListsEnabled}
                                    onChange={(e) => setOrganizationFormData({ ...organizationFormData, priceListsEnabled: e.target.checked })}
                                  />
                                  <Form.Text className="text-muted">
                                    Adds a Price Lists tab to Sales and a price list choice on each customer. Quotations, sale orders and sale bills then start
                                    from the price of the customer&apos;s list. Most businesses don&apos;t need it.
                                  </Form.Text>
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Two-step sign-in</Form.Label>
                                  <Form.Check
                                    type="switch"
                                    id="org-two-factor-required"
                                    label="Everyone must sign in with a code from an authenticator app"
                                    checked={organizationFormData.twoFactorRequired}
                                    onChange={(e) => setOrganizationFormData({ ...organizationFormData, twoFactorRequired: e.target.checked })}
                                  />
                                  <Form.Text className="text-muted">
                                    As well as their password. Anyone who has not set it up is walked through it at their next sign-in.
                                    Each person can already turn it on for themselves in Settings.
                                    {twoFactorOn.length > 0 && ` ${twoFactorOn.length} of ${users.length || '?'} people have it on now.`}
                                  </Form.Text>
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>TDS</Form.Label>
                                  <Form.Check
                                    type="switch"
                                    id="org-tds-enabled"
                                    label="We deduct TDS when paying vendors"
                                    checked={organizationFormData.tdsEnabled}
                                    onChange={(e) => setOrganizationFormData({ ...organizationFormData, tdsEnabled: e.target.checked })}
                                  />
                                  <Form.Text className="text-muted">
                                    Most small businesses don&apos;t need this. Turn it on if your CA says you must deduct TDS on payments to
                                    contractors, professionals, rent or commission; Accounts then gets a TDS tab.
                                  </Form.Text>
                                  {organizationFormData.tdsEnabled && (
                                    <Form.Control
                                      className="mt-2"
                                      type="text"
                                      value={organizationFormData.tan}
                                      maxLength={10}
                                      onChange={(e) => setOrganizationFormData({ ...organizationFormData, tan: e.target.value.toUpperCase() })}
                                      placeholder="TAN, like DELA12345B (on your TDS challans)"
                                    />
                                  )}
                                </Form.Group>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>UPI ID for payments</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={organizationFormData.upiId}
                                    maxLength={100}
                                    onChange={(e) => setOrganizationFormData({ ...organizationFormData, upiId: e.target.value.trim() })}
                                    placeholder="e.g. shreemills@okhdfcbank"
                                  />
                                  <Form.Text className="text-muted">
                                    Optional. Statements and invoices you send then show a &ldquo;Pay by UPI&rdquo; button, and printed invoices a QR code, for the amount due.
                                  </Form.Text>
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Bank account on invoices</Form.Label>
                                  <Form.Select
                                    value={organizationFormData.invoiceBankAccountId}
                                    onChange={(e) => setOrganizationFormData({ ...organizationFormData, invoiceBankAccountId: e.target.value })}
                                  >
                                    <option value="">Don&apos;t print bank details</option>
                                    {invoiceBankAccounts.map((b) => (
                                      <option key={b.id} value={b.id}>{b.accountName} · {b.bankName} ··{String(b.accountNumber || '').slice(-4)}</option>
                                    ))}
                                  </Form.Select>
                                  <Form.Text className="text-muted">Printed on tax invoices for customers to pay into (add accounts under Accounts &rsaquo; Bank Accounts).</Form.Text>
                                </Form.Group>
                              </Col>
                            </Row>
                            <Form.Group className="mb-3">
                              <Form.Label>Terms on invoices</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                maxLength={2000}
                                value={organizationFormData.invoiceTerms}
                                onChange={(e) => setOrganizationFormData({ ...organizationFormData, invoiceTerms: e.target.value })}
                                placeholder={'e.g. 1. Interest at 18% a year on bills unpaid after the due date.\n2. Subject to Mysuru jurisdiction.'}
                              />
                              <Form.Text className="text-muted">Printed at the foot of every tax invoice.</Form.Text>
                            </Form.Group>
                            <GstApiSettingsCard />
                          </>
                        )}

                        <Form.Group className="mb-3">
                          <Form.Label>Terms on purchase orders</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            maxLength={2000}
                            value={organizationFormData.purchaseOrderTerms}
                            onChange={(e) => setOrganizationFormData({ ...organizationFormData, purchaseOrderTerms: e.target.value })}
                            placeholder={'e.g. 1. Goods to be delivered between 9 am and 5 pm.\n2. Material not matching the order will be returned.'}
                          />
                          <Form.Text className="text-muted">Printed at the foot of every purchase order sent to a vendor.</Form.Text>
                        </Form.Group>

                        <hr className="my-4" />

                        <h6 className="mb-3">Preferences</h6>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Default Currency</Form.Label>
                              <Form.Select
                                value={organizationFormData.defaultCurrency}
                                onChange={(e) => setOrganizationFormData({ ...organizationFormData, defaultCurrency: e.target.value })}
                              >
                                <option value="">Select Currency</option>
                                {currencyCodes.map(currency => (
                                  <option key={currency.code} value={currency.code}>
                                    {currency.code} - {currency.name}
                                  </option>
                                ))}
                              </Form.Select>
                              <Form.Text className="text-muted">
                                This currency will be used as the default for all financial transactions in your organization.
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Date format</Form.Label>
                              <Form.Select
                                value={organizationFormData.dateFormat}
                                onChange={(e) => setOrganizationFormData({ ...organizationFormData, dateFormat: e.target.value })}
                              >
                                <option value="">
                                  Usual for {organizationFormData.country || 'your country'} ({formatDate('2026-10-18', defaultDateFormat(organizationFormData.country))})
                                </option>
                                {DATE_FORMATS.map((f) => (
                                  <option key={f} value={f}>{f.replace('DD', 'dd').replace('MM', 'mm').replace('YYYY', 'yyyy')} ({formatDate('2026-10-18', f)})</option>
                                ))}
                              </Form.Select>
                              <Form.Text className="text-muted">
                                How dates are shown and typed for everyone in your organization. Documents that spell the month, such as 18 Oct 2026, are not affected.
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Logo URL</Form.Label>
                              <WebAddressInput
                                value={organizationFormData.logoUrl}
                                onChange={(v) => setOrganizationFormData({ ...organizationFormData, logoUrl: v })}
                                placeholder="example.com/logo.png"
                              />
                              <Form.Text className="text-muted">
                                HTTP URL of your organization's logo image.
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>

                        <div className="mt-4">
                          <Button 
                            variant="primary" 
                            type="submit" 
                            disabled={savingSettings}
                          >
                            {savingSettings ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Saving...
                              </>
                            ) : (
                              'Save Settings'
                            )}
                          </Button>
                          <Button 
                            variant="secondary" 
                            className="ms-2"
                            onClick={fetchOrganizationSettings}
                            disabled={savingSettings}
                          >
                            Cancel
                          </Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>
                )}
              </div>
            </Tab.Pane>

            {/* Users Tab */}
            <Tab.Pane eventKey="users">
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="text-muted mb-0">Manage users in your organization</p>
                  <Button variant="primary" onClick={handleCreateUser}>
                    + Create User
                  </Button>
                </div>
                {loading ? (
                  <div className="d-flex justify-content-center">
                    <Spinner animation="border" role="status" />
                  </div>
                ) : (
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Roles</th>
                        <th>Status</th>
                        <th>Last sign-in</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center text-muted">
                            No users found. Add a user to get started.
                          </td>
                        </tr>
                      ) : (
                        [...users].sort((a, b) => (Number(b.isActive !== false) - Number(a.isActive !== false))
                          || `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(`${b.firstName || ''} ${b.lastName || ''}`, undefined, { sensitivity: 'base' })).map((u) => (
                          <tr key={u.id}>
                            <td>{u.email}</td>
                            <td>{u.firstName} {u.lastName}</td>
                            <td>{u.phoneNumber || '-'}</td>
                            <td>
                              {u.roles && u.roles.length > 0 ? (
                                u.roles.map((role, idx) => {
                                  const roleName = typeof role === 'string' 
                                    ? role 
                                    : (role.name || (typeof role === 'object' && role.id ? `Role ${role.id}` : 'Unknown'));
                                  return (
                                    <Badge key={idx} bg="secondary" className="me-1">
                                      {getRoleLabel(typeof role === 'object' ? role : roleName)}
                                    </Badge>
                                  );
                                })
                              ) : (
                                <span className="text-muted">No roles</span>
                              )}
                            </td>
                            <td>
                              {u.isActive ? (
                                <Badge bg="success">Active</Badge>
                              ) : (
                                <Badge bg="danger">Inactive</Badge>
                              )}
                              {twoFactorOn.includes(u.id) && <Badge bg="info" className="ms-1" title="Signs in with a code from an authenticator app">2-step</Badge>}
                            </td>
                            <td className="text-nowrap">
                              {u.lastLogin
                                ? formatDateTime(u.lastLogin)
                                : <span className="text-muted" title="They have not signed in yet. If they never got the invitation, open Edit and email a new set-password link.">Not yet</span>}
                            </td>
                            <td className="text-nowrap">
                              <Button
                                size="sm"
                                variant="outline-info"
                                className="me-2"
                                onClick={() => handleManageUserRoles(u)}
                                title="Roles: choose what this person can do"
                                aria-label={`Roles of ${u.firstName || ''} ${u.lastName || ''}`.trim()}
                                data-testid="user-roles-button"
                              >
                                <i className="bi bi-person-badge" aria-hidden="true" style={{ fontSize: 16, verticalAlign: 'middle' }} />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-info"
                                className="me-2"
                                onClick={() => handleViewPrint(u, 'USER')}
                                title="View & Print"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                  <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                  <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                </svg>
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="me-2"
                                onClick={() => handleEditUser(u)}
                                title="Edit"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                  <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                  <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                </svg>
                              </Button>
                              {u.id !== user?.id && (
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleDeleteUser(u)}
                                title="Remove from this organization"
                                aria-label={`Remove ${u.firstName || ''} ${u.lastName || ''}`.trim()}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                  <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                </svg>
                              </Button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                )}
              </div>
            </Tab.Pane>

            {/* Roles Tab */}
            <Tab.Pane eventKey="roles">
              <div className="mt-3">
                <FeatureNotice feature="CUSTOM_ROLES" className="mb-3">You can use and edit the standard roles, but you can’t add new ones.</FeatureNotice>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="text-muted mb-0">Manage roles and their access permissions for your organization</p>
                  <Button variant="primary" onClick={handleCreateRole}>
                    + Create Role
                  </Button>
                </div>

                {loading ? (
                  <div className="d-flex justify-content-center">
                    <Spinner animation="border" role="status" />
                  </div>
                ) : (
                  <Card>
                    <Card.Header>
                      <h5 className="mb-0">Organization Roles</h5>
                    </Card.Header>
                    <Card.Body>
                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Description</th>
                            <th>System Role</th>
                            <th>Status</th>
                            <th>Permissions</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {roles.length === 0 ? (
                            <tr>
                              <td colSpan="6" className="text-center text-muted">
                                No roles found. Create your first role to get started.
                              </td>
                            </tr>
                          ) : (
                            roles.map((role) => (
                              <tr key={role.id}>
                                <td><strong>{getRoleLabel(role)}</strong></td>
                                <td>{role.description || '-'}</td>
                                <td>
                                  {role.isSystemRole ? (
                                    <Badge bg="info">System</Badge>
                                  ) : (
                                    <Badge bg="secondary">Custom</Badge>
                                  )}
                                </td>
                                <td>
                                  {role.isActive ? (
                                    <Badge bg="success">Active</Badge>
                                  ) : (
                                    <Badge bg="danger">Inactive</Badge>
                                  )}
                                </td>
                                <td>
                                  {role.permissions ? `${role.permissions.length} permission(s)` : '0 permissions'}
                                </td>
                                <td>
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    className="me-2"
                                    onClick={() => handleManagePermissions(role)}
                                    title="Manage Permissions"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM5 8h6a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/>
                                    </svg>
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-info"
                                    className="me-2"
                                    onClick={() => handleViewPrint(role, 'ROLE')}
                                    title="View & Print"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                      <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                    </svg>
                                  </Button>
                                  {!role.isSystemRole && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        className="me-2"
                                        onClick={() => handleEditRole(role)}
                                        title="Edit"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                          <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                          <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                        </svg>
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => handleDeleteRole(role.id)}
                                        title="Delete"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                          <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                          <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                        </svg>
                                      </Button>
                                    </>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
              </div>
            </Tab.Pane>

            <Tab.Pane eventKey="online-store">
              {activeTab === 'online-store' && <OnlineStoreSettings />}
            </Tab.Pane>

            {/* Retail Stores Tab */}
            <Tab.Pane eventKey="retail-stores">
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="text-muted mb-0">Manage retail store locations where your organization sells goods to retail customers</p>
                  <Button variant="primary" onClick={handleCreateRetailStore}>
                    + Create Retail Store
                  </Button>
                </div>

                {loading ? (
                  <div className="d-flex justify-content-center">
                    <Spinner animation="border" role="status" />
                  </div>
                ) : (
                  <Card>
                    <Card.Body>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>City</th>
                            <th>State</th>
                            <th>Country</th>
                            <th>Phone</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {retailStores.map((retailStore) => (
                            <tr key={retailStore.id}>
                              <td>{retailStore.name}</td>
                              <td>{retailStore.city || '-'}</td>
                              <td>{retailStore.state || '-'}</td>
                              <td>{retailStore.country || '-'}</td>
                              <td>{retailStore.phoneNumber || '-'}</td>
                              <td>
                                <Badge bg={retailStore.isActive ? 'success' : 'secondary'}>
                                  {retailStore.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-info"
                                  className="me-2"
                                  onClick={() => handleViewPrint(retailStore, 'RETAIL_STORE')}
                                  title="View & Print"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                    <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  className="me-2"
                                  onClick={() => handleEditRetailStore(retailStore)}
                                  title="Edit"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeleteRetailStore(retailStore.id)}
                                  title="Delete"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                  </svg>
                                </Button>
                              </td>
                            </tr>
                          ))}
                          {retailStores.length === 0 && (
                            <tr>
                              <td colSpan="7" className="text-center text-muted">
                                No retail stores found. Create your first retail store to get started.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
              </div>
            </Tab.Pane>

            {/* Warehouses Tab */}
            <Tab.Pane eventKey="warehouses">
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="text-muted mb-0">Manage warehouse locations for your organization</p>
                  <Button variant="primary" onClick={handleCreateWarehouse}>
                    + Create Warehouse
                  </Button>
                </div>

                {loading ? (
                  <div className="d-flex justify-content-center">
                    <Spinner animation="border" role="status" />
                  </div>
                ) : (
                  <Card>
                    <Card.Body>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>City</th>
                            <th>State</th>
                            <th>Country</th>
                            <th>Phone</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {warehouses.map((warehouse) => (
                            <tr key={warehouse.id}>
                              <td>{warehouse.name}</td>
                              <td>{warehouse.city || '-'}</td>
                              <td>{warehouse.state || '-'}</td>
                              <td>{warehouse.country || '-'}</td>
                              <td>{warehouse.phoneNumber || '-'}</td>
                              <td>
                                <Badge bg={warehouse.isActive ? 'success' : 'secondary'}>
                                  {warehouse.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-info"
                                  className="me-2"
                                  onClick={() => handleViewPrint(warehouse, 'WAREHOUSE')}
                                  title="View & Print"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                    <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleEditWarehouse(warehouse)}
                                  title="Edit"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeleteWarehouse(warehouse.id)}
                                  title="Delete"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                  </svg>
                                </Button>
                              </td>
                            </tr>
                          ))}
                          {warehouses.length === 0 && (
                            <tr>
                              <td colSpan="7" className="text-center text-muted">
                                No warehouses found. Create one to get started.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
              </div>
            </Tab.Pane>

            {/* Production Units Tab */}
            <Tab.Pane eventKey="production-units">
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="text-muted mb-0">Manage production unit locations for your organization</p>
                  <Button variant="primary" onClick={handleCreateProductionUnit}>
                    + Create Production Unit
                  </Button>
                </div>

                {loading ? (
                  <div className="d-flex justify-content-center">
                    <Spinner animation="border" role="status" />
                  </div>
                ) : (
                  <Card>
                    <Card.Body>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>City</th>
                            <th>State</th>
                            <th>Country</th>
                            <th>Phone</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productionUnits.map((productionUnit) => (
                            <tr key={productionUnit.id}>
                              <td>{productionUnit.name}</td>
                              <td>{productionUnit.city || '-'}</td>
                              <td>{productionUnit.state || '-'}</td>
                              <td>{productionUnit.country || '-'}</td>
                              <td>{productionUnit.phoneNumber || '-'}</td>
                              <td>
                                <Badge bg={productionUnit.isActive ? 'success' : 'secondary'}>
                                  {productionUnit.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-info"
                                  className="me-2"
                                  onClick={() => handleViewPrint(productionUnit, 'PRODUCTION_UNIT')}
                                  title="View & Print"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                    <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleEditProductionUnit(productionUnit)}
                                  title="Edit"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeleteProductionUnit(productionUnit.id)}
                                  title="Delete"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                  </svg>
                                </Button>
                              </td>
                            </tr>
                          ))}
                          {productionUnits.length === 0 && (
                            <tr>
                              <td colSpan="7" className="text-center text-muted">
                                No production units found. Create one to get started.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
              </div>
            </Tab.Pane>

            {/* Organization Materials Tab */}
            <Tab.Pane eventKey="organization-materials">
              <MaterialRequestUpdates />
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="text-muted mb-0">Your materials, with your own SKU, unit and reorder levels. Add more from the Global Material Master below.</p>
                  <Button variant="outline-primary" onClick={handleCreateMaterial}>
                    + Create my own material
                  </Button>
                </div>

                {loading ? (
                  <div className="d-flex justify-content-center">
                    <Spinner animation="border" role="status" />
                  </div>
                ) : (
                  <Card>
                    <Card.Body>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Item Name</th>
                            <th>Category</th>
                            <th>SKU</th>
                            <th>MOQ</th>
                            <th>SSQ</th>
                            <th>ROP</th>
                            <th>POQ</th>
                            <th>UOM</th>
                            <th>GST</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {organizationMaterials.map((material) => (
                            <tr key={material.id}>
                              <td>
                                {material.item?.name || '-'}
                                {material.item?.genericItemName && <div className="small text-muted">filed under {material.item.genericItemName}</div>}
                              </td>
                              <td>{material.item?.itemType?.name || '-'}</td>
                              <td>{material.sku || '-'}</td>
                              <td>{material.moq || '-'}</td>
                              <td>{material.ssq || '-'}</td>
                              <td>{material.rop || '-'}</td>
                              <td>{material.poq || '-'}</td>
                              <td>{material.unitOfMeasurement ? (material.unitOfMeasurement.symbol || material.unitOfMeasurement.name) : '-'}</td>
                              <td>{material.gstRate != null ? `${Number(material.gstRate)}%` : '-'}</td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-info"
                                  className="me-2"
                                  onClick={() => handleViewPrint(material, 'MATERIAL')}
                                  title="View & Print"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                    <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleEditMaterial(material)}
                                  title="Edit"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeleteMaterial(material.id)}
                                  title="Delete"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                  </svg>
                                </Button>
                                <HistoryButton documentType="OrganizationMaterial" documentId={material.id} label={material.item?.name} className="ms-1" />
                              </td>
                            </tr>
                          ))}
                          {organizationMaterials.length === 0 && (
                            <tr>
                              <td colSpan="10" className="text-center text-muted">
                                No materials yet. Add some from the Global Material Master below.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
                <MaterialCatalogBrowser
                  itemTypes={itemTypes}
                  reloadKey={catalogReloadKey}
                  startOpen
                  onAdd={handleAddFromCatalog}
                  onMakeVariant={handleMakeVariant}
                  onCreateOwn={handleCreateMaterial}
                />
              </div>
            </Tab.Pane>

            {/* Organization Services Tab */}
            <Tab.Pane eventKey="organization-services">
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="text-muted mb-0">Manage organization-specific service pricing and attributes</p>
                  <Button variant="primary" onClick={handleCreateService}>
                    + Add Service
                  </Button>
                </div>

                {loading ? (
                  <div className="d-flex justify-content-center">
                    <Spinner animation="border" role="status" />
                  </div>
                ) : (
                  <Card>
                    <Card.Body>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Service Name</th>
                            <th>Service Type</th>
                            <th>Pricing Model</th>
                            <th>Hourly Rate</th>
                            <th>Daily Rate</th>
                            <th>Fixed Price</th>
                            <th>Est. Duration (hrs)</th>
                            <th>UOM</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {organizationServices.map((orgService) => (
                            <tr key={orgService.id}>
                              <td>{orgService.service?.name || '-'}</td>
                              <td>{orgService.service?.serviceType?.name || '-'}</td>
                              <td>{orgService.pricingModel || '-'}</td>
                              <td>{orgService.hourlyRate ? formatOrgMoney(orgService.hourlyRate) : '-'}</td>
                              <td>{orgService.dailyRate ? formatOrgMoney(orgService.dailyRate) : '-'}</td>
                              <td>{orgService.fixedPrice ? formatOrgMoney(orgService.fixedPrice) : '-'}</td>
                              <td>{orgService.estimatedDurationHours || '-'}</td>
                              <td>{orgService.unitOfMeasurement || '-'}</td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-info"
                                  className="me-2"
                                  onClick={() => handleViewPrint(orgService, 'SERVICE')}
                                  title="View & Print"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                    <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleEditService(orgService)}
                                  title="Edit"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeleteService(orgService.id)}
                                  title="Delete"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                  </svg>
                                </Button>
                              </td>
                            </tr>
                          ))}
                          {organizationServices.length === 0 && (
                            <tr>
                              <td colSpan="9" className="text-center text-muted">
                                No organization services found. Add one to get started.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
              </div>
            </Tab.Pane>

            {/* Import / Export Tab */}
            <Tab.Pane eventKey="import-export">
              <DataImportExport />
            </Tab.Pane>

            <Tab.Pane eventKey="edit-log">
              <EditLog active={activeTab === 'edit-log'} />
            </Tab.Pane>

            {/* Quality Grades Tab */}
            <Tab.Pane eventKey="quality-grades">
              <OrganizationQualityGrades currencySymbol={orgCurrencyCode ? `${orgCurrencyCode} ` : ''} />
            </Tab.Pane>

            {/* Products Tab */}
            <Tab.Pane eventKey="products">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <p className="text-muted mb-0">Manage the products you sell. Add one from the Global Material Master below, or create your own.</p>
                  {storeEnabled && <p className="small text-muted mb-0" data-testid="products-store-note">Your online store shows only the products you publish: tick them under <a href="/store/products">Online Store &rarr; Products online</a> (they need a price).</p>}
                </div>
                <Button variant="primary" onClick={handleCreateProduct}>
                  + Create my own product
                </Button>
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <Card>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Item (Material Master)</th>
                          <th>Name</th>
                          <th>SKU</th>
                          <th>Default sale price</th>
                          <th>Category</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center text-muted">
                              No products found. Create one to get started.
                            </td>
                          </tr>
                        ) : (
                          products.map((product) => (
                            <tr key={product.id}>
                              <td>
                                {product.item ? (
                                  <span>
                                    <strong>{product.item.name}</strong>
                                    {product.item.itemType && (
                                      <Badge bg="info" className="ms-2">{product.item.itemType.name}</Badge>
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-muted">Not linked</span>
                                )}
                              </td>
                              <td>{product.name}</td>
                              <td>{product.sku || '-'}</td>
                              <td>{formatProductSalePrice(product.price)}</td>
                              <td>{product.category || product.item?.itemType?.name || '-'}</td>
                              <td>
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => handleViewPrint(product, 'PRODUCT')}
                                  title="View & Print"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                    <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                  </svg>
                                </Button>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => handleEditProduct(product)}
                                  title="Edit"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                  </svg>
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteProduct(product.id)}
                                  title="Delete"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                  </svg>
                                </Button>
                                <HistoryButton documentType="Product" documentId={product.id} label={product.name} className="ms-1" />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}

              <MaterialCatalogBrowser
                forProducts
                itemTypes={itemTypes}
                reloadKey={productCatalogReloadKey}
                startOpen
                onAdd={handleAddProductFromCatalog}
                onCreateOwn={handleCreateProduct}
              />

              {/* Product Modal */}
              <Modal show={showProductModal} onHide={handleCloseProductModal} size="lg">
                <Modal.Header closeButton>
                  <Modal.Title>
                    {editingProduct ? 'Edit Product' : 'Add Product'}
                  </Modal.Title>
                </Modal.Header>
                <Form ref={productFormRef} onSubmit={handleProductSubmit}>
                  <Modal.Body>
                    <p className="text-muted small">
                      A product is something you sell. Every product is linked to one item in the Global Material Master, the single
                      list that purchase, stock, production and sales all use, so the same item can be bought, made or sold.
                    </p>
                    {!editingProduct && (
                      <div className="mb-3">
                        {productCreateMode === 'existing' ? (
                          <Button variant="link" size="sm" className="p-0" onClick={() => setProductCreateMode('draft')}>
                            Can't find it? Add a new item for this product
                          </Button>
                        ) : (
                          <Button variant="link" size="sm" className="p-0" onClick={() => setProductCreateMode('existing')}>
                            ← Back to selecting an existing item
                          </Button>
                        )}
                      </div>
                    )}

                    {productCreateMode === 'draft' && !editingProduct && (
                      <>
                        <p className="text-muted small">
                          This adds the item as your own in the Material Master and creates the product in one step, ready to use
                          right away; no System Admin review needed.
                        </p>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Product name *</Form.Label>
                              <Form.Control
                                type="text"
                                value={draftProductItemForm.itemName}
                                onChange={(e) => setDraftProductItemForm({ ...draftProductItemForm, itemName: e.target.value })}
                                required
                              />
                              <SimilarItemsHint
                                name={draftProductItemForm.itemName}
                                items={materialMasterItems}
                                onUse={(item) => {
                                  setProductCreateMode('existing');
                                  setPickedProductItem({ id: item.id, name: item.name, category: item.itemType?.name, hsCode: item.hsCode });
                                  setProductFormData({ ...productFormData, itemId: String(item.id), name: productFormData.name || item.name });
                                }}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Category *</Form.Label>
                              <CategorySelect
                                itemTypes={itemTypes}
                                value={draftProductItemForm.itemTypeId}
                                onChange={(e) => setDraftProductItemForm({ ...draftProductItemForm, itemTypeId: e.target.value })}
                                onCreated={(newType) => {
                                  setItemTypes((prev) => [...prev, newType]);
                                  setDraftProductItemForm({ ...draftProductItemForm, itemTypeId: newType.id.toString() });
                                }}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>HSN code (optional)</Form.Label>
                              <HsnLookupInput
                                name="hsCode"
                                value={draftProductItemForm.hsCode}
                                onChange={(e) => setDraftProductItemForm({ ...draftProductItemForm, hsCode: e.target.value })}
                                nameValue={draftProductItemForm.itemName}
                                onUseName={(itemName) => setDraftProductItemForm((f) => ({ ...f, itemName }))}
                                onSelect={(r) => {
                                  const patch = categoryFromHsn(draftProductItemForm, r);
                                  setDraftProductItemForm((f) => ({ ...f, hsCode: r.code, ...patch }));
                                }}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>GTIN / barcode (optional)</Form.Label>
                              <Form.Control
                                type="text"
                                value={draftProductItemForm.gtin}
                                onChange={(e) => setDraftProductItemForm({ ...draftProductItemForm, gtin: e.target.value })}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </>
                    )}

                    {productCreateMode === 'existing' && (
                      <div className="border rounded p-2 mb-3">
                        <div className="small text-muted">Item from the Global Material Master</div>
                        <strong>{pickedProductItem?.name || editingProduct?.item?.name || '-'}</strong>
                        {(pickedProductItem?.category || editingProduct?.item?.itemType?.name) && (
                          <Badge bg="info" className="ms-2">{pickedProductItem?.category || editingProduct?.item?.itemType?.name}</Badge>
                        )}
                        {(pickedProductItem?.hsCode || editingProduct?.item?.hsCode) && (
                          <span className="small text-muted ms-2">HSN {pickedProductItem?.hsCode || editingProduct?.item?.hsCode}</span>
                        )}
                        <div className="small text-muted mt-1">
                          {editingProduct
                            ? 'The item cannot be changed after the product is made.'
                            : 'The same item can also be bought or made, not only sold.'}
                        </div>
                      </div>
                    )}
                    <Row>
                      {(productCreateMode === 'existing' || editingProduct) && (
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Product name *</Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={productFormData.name}
                            onChange={handleProductInputChange}
                            required
                          />
                          <Form.Text className="text-muted">
                            Filled in from the item. Change it only if you sell it under another name.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      )}
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>SKU</Form.Label>
                          <Form.Control
                            type="text"
                            name="sku"
                            value={productFormData.sku}
                            onChange={handleProductInputChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="description"
                        value={productFormData.description}
                        onChange={handleProductInputChange}
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{rateFieldLabel('Default sale price')}</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            name="price"
                            value={productFormData.price}
                            onChange={handleProductInputChange}
                            placeholder="Used as unit price in Sales orders"
                          />
                          <Form.Text className="text-muted">
                            Pre-fills unit price when this product is added on Sales → Orders. You can still change it per order.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{rateFieldLabel('Cost price')}</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            name="costPrice"
                            value={productFormData.costPrice}
                            onChange={handleProductInputChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>


                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Brand</Form.Label>
                          <Form.Control
                            type="text"
                            name="brand"
                            value={productFormData.brand}
                            onChange={handleProductInputChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    {!editingProduct && (
                      <>
                        <OpeningStockFields
                          form={productOpeningStock}
                          onChange={setProductOpeningStock}
                          info={productOpeningInfo}
                          unitLabel={(() => {
                            const u = uoms.find((x) => String(x.id) === String(productUnitId));
                            return u ? (u.symbol || u.name) : '';
                          })()}
                          currency={orgCurrencyCode}
                        />
                        {productOpeningStock.on && (
                          <Form.Group className="mb-3">
                            <Form.Label>Unit of measurement</Form.Label>
                            <Form.Select value={productUnitId} onChange={(e) => setProductUnitId(e.target.value)}>
                              <option value="">Choose the unit</option>
                              {uoms.map((u) => <option key={u.id} value={u.id}>{u.name}{u.symbol ? ` (${u.symbol})` : ''}</option>)}
                            </Form.Select>
                            <Form.Text className="text-muted">The unit the opening stock quantity is counted in.</Form.Text>
                          </Form.Group>
                        )}
                      </>
                    )}
                    {!editingProduct && (
                      <Form.Text className="text-muted d-block mt-3">After saving, open the product with Edit to add photos and a video link.</Form.Text>
                    )}
                    {editingProduct && (
                      <div className="mt-3">
                        <Form.Label>Photos</Form.Label>
                        <ProductPhotos productId={editingProduct.id} />
                        <Form.Text className="text-muted d-block mt-1">
                          Shown in the product catalog you send to customers (Sales &rsaquo; Product Catalog &rsaquo; Share catalog).
                        </Form.Text>
                        <Form.Group className="mt-3">
                          <Form.Label>Video link (optional)</Form.Label>
                          <Form.Control
                            type="url"
                            name="videoUrl"
                            value={productFormData.videoUrl || ''}
                            onChange={handleProductInputChange}
                            placeholder="https://www.youtube.com/watch?v=..."
                            data-testid="product-video-url"
                          />
                          <Form.Text className="text-muted">A YouTube or Vimeo link. Customers see a Watch video button on the catalog.</Form.Text>
                        </Form.Group>
                      </div>
                    )}
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseProductModal}>
                      Cancel
                    </Button>
                    {!editingProduct && productCreateMode === 'draft' && (
                      <Button
                        variant="outline-primary"
                        type="button"
                        disabled={savingDraftProduct}
                        onClick={() => { productAnotherRef.current = true; productFormRef.current?.requestSubmit(); }}
                      >
                        Save and add another
                      </Button>
                    )}
                    <Button variant="primary" type="submit" disabled={productCreateMode === 'draft' && savingDraftProduct}
                      onClick={() => { productAnotherRef.current = false; }}>
                      {productCreateMode === 'draft'
                        ? (savingDraftProduct ? 'Creating...' : 'Create product')
                        : (editingProduct ? 'Update' : 'Create')}
                    </Button>
                  </Modal.Footer>
                </Form>
              </Modal>
            </Tab.Pane>
            </Tab.Content>
          </div>
        </div>
        </Tab.Container>

      {/* Warehouse Modal */}
      <Modal show={showWarehouseModal} onHide={() => setShowWarehouseModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedWarehouse ? 'Edit Warehouse' : 'Create Warehouse'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveWarehouse}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={warehouseFormData.name}
                    onChange={(e) => setWarehouseFormData({ ...warehouseFormData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={warehouseFormData.isActive ? 'true' : 'false'}
                    onChange={(e) => setWarehouseFormData({ ...warehouseFormData, isActive: e.target.value === 'true' })}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={warehouseFormData.description}
                onChange={(e) => setWarehouseFormData({ ...warehouseFormData, description: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    value={warehouseFormData.address}
                    onChange={(e) => setWarehouseFormData({ ...warehouseFormData, address: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    value={warehouseFormData.city}
                    onChange={(e) => setWarehouseFormData({ ...warehouseFormData, city: e.target.value })}
                    placeholder="City"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>District</Form.Label>
                  <Form.Control
                    type="text"
                    value={warehouseFormData.district}
                    onChange={(e) => setWarehouseFormData({ ...warehouseFormData, district: e.target.value })}
                    placeholder="District"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>State/Province</Form.Label>
                  {selectedWarehouseCountryCode && selectedWarehouseCountryCode !== 'OTHER' && getStatesForCountry(selectedWarehouseCountryCode).length > 0 ? (
                    <Form.Select
                      value={warehouseFormData.state}
                      onChange={(e) => setWarehouseFormData({ ...warehouseFormData, state: e.target.value })}
                    >
                      <option value="">Select State/Province</option>
                      {getStatesForCountry(selectedWarehouseCountryCode).map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="text"
                      value={warehouseFormData.state}
                      onChange={(e) => setWarehouseFormData({ ...warehouseFormData, state: e.target.value })}
                      placeholder="State/Province"
                    />
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Select
                    value={selectedWarehouseCountryCode}
                    onChange={(e) => {
                      const countryCode = e.target.value;
                      const countryObj = countries.find(c => c.code === countryCode);
                      const countryName = countryObj ? countryObj.name : '';
                      setSelectedWarehouseCountryCode(countryCode);
                      setWarehouseFormData({
                        ...warehouseFormData,
                        country: countryName,
                        state: '' // Reset state when country changes
                      });
                    }}
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country.code} value={country.code}>{country.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={warehouseFormData.postalCode}
                    onChange={(e) => setWarehouseFormData({ ...warehouseFormData, postalCode: e.target.value })}
                    placeholder="Postal Code"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <PhoneNumberInput
                    name="phoneNumber"
                    value={warehouseFormData.phoneNumber}
                    onChange={(e) => setWarehouseFormData({ ...warehouseFormData, phoneNumber: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={warehouseFormData.email}
                    onChange={(e) => setWarehouseFormData({ ...warehouseFormData, email: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowWarehouseModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedWarehouse ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Retail Store Modal */}
      <Modal show={showRetailStoreModal} onHide={() => setShowRetailStoreModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedRetailStore ? 'Edit Retail Store' : 'Create Retail Store'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveRetailStore}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={retailStoreFormData.name}
                    onChange={(e) => setRetailStoreFormData({ ...retailStoreFormData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={retailStoreFormData.isActive ? 'true' : 'false'}
                    onChange={(e) => setRetailStoreFormData({ ...retailStoreFormData, isActive: e.target.value === 'true' })}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={retailStoreFormData.description}
                onChange={(e) => setRetailStoreFormData({ ...retailStoreFormData, description: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    value={retailStoreFormData.address}
                    onChange={(e) => setRetailStoreFormData({ ...retailStoreFormData, address: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    value={retailStoreFormData.city}
                    onChange={(e) => setRetailStoreFormData({ ...retailStoreFormData, city: e.target.value })}
                    placeholder="City"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>District</Form.Label>
                  <Form.Control
                    type="text"
                    value={retailStoreFormData.district}
                    onChange={(e) => setRetailStoreFormData({ ...retailStoreFormData, district: e.target.value })}
                    placeholder="District"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>State/Province</Form.Label>
                  {selectedRetailStoreCountryCode && selectedRetailStoreCountryCode !== 'OTHER' && getStatesForCountry(selectedRetailStoreCountryCode).length > 0 ? (
                    <Form.Select
                      value={retailStoreFormData.state}
                      onChange={(e) => setRetailStoreFormData({ ...retailStoreFormData, state: e.target.value })}
                    >
                      <option value="">Select State/Province</option>
                      {getStatesForCountry(selectedRetailStoreCountryCode).map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="text"
                      value={retailStoreFormData.state}
                      onChange={(e) => setRetailStoreFormData({ ...retailStoreFormData, state: e.target.value })}
                      placeholder="State/Province"
                    />
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Select
                    value={selectedRetailStoreCountryCode}
                    onChange={(e) => {
                      const countryCode = e.target.value;
                      const countryObj = countries.find(c => c.code === countryCode);
                      const countryName = countryObj ? countryObj.name : '';
                      setSelectedRetailStoreCountryCode(countryCode);
                      setRetailStoreFormData({
                        ...retailStoreFormData,
                        country: countryName,
                        state: ''
                      });
                    }}
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country.code} value={country.code}>{country.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={retailStoreFormData.postalCode}
                    onChange={(e) => setRetailStoreFormData({ ...retailStoreFormData, postalCode: e.target.value })}
                    placeholder="Postal Code"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <PhoneNumberInput
                    name="phoneNumber"
                    value={retailStoreFormData.phoneNumber}
                    onChange={(e) => setRetailStoreFormData({ ...retailStoreFormData, phoneNumber: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={retailStoreFormData.email}
                    onChange={(e) => setRetailStoreFormData({ ...retailStoreFormData, email: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowRetailStoreModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedRetailStore ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Production Unit Modal */}
      <Modal show={showProductionUnitModal} onHide={() => setShowProductionUnitModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedProductionUnit ? 'Edit Production Unit' : 'Create Production Unit'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveProductionUnit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={productionUnitFormData.name}
                    onChange={(e) => setProductionUnitFormData({ ...productionUnitFormData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={productionUnitFormData.isActive ? 'true' : 'false'}
                    onChange={(e) => setProductionUnitFormData({ ...productionUnitFormData, isActive: e.target.value === 'true' })}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={productionUnitFormData.description}
                onChange={(e) => setProductionUnitFormData({ ...productionUnitFormData, description: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    value={productionUnitFormData.address}
                    onChange={(e) => setProductionUnitFormData({ ...productionUnitFormData, address: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    value={productionUnitFormData.city}
                    onChange={(e) => setProductionUnitFormData({ ...productionUnitFormData, city: e.target.value })}
                    placeholder="City"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>District</Form.Label>
                  <Form.Control
                    type="text"
                    value={productionUnitFormData.district}
                    onChange={(e) => setProductionUnitFormData({ ...productionUnitFormData, district: e.target.value })}
                    placeholder="District"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>State/Province</Form.Label>
                  {selectedProductionUnitCountryCode && selectedProductionUnitCountryCode !== 'OTHER' && getStatesForCountry(selectedProductionUnitCountryCode).length > 0 ? (
                    <Form.Select
                      value={productionUnitFormData.state}
                      onChange={(e) => setProductionUnitFormData({ ...productionUnitFormData, state: e.target.value })}
                    >
                      <option value="">Select State/Province</option>
                      {getStatesForCountry(selectedProductionUnitCountryCode).map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="text"
                      value={productionUnitFormData.state}
                      onChange={(e) => setProductionUnitFormData({ ...productionUnitFormData, state: e.target.value })}
                      placeholder="State/Province"
                    />
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Select
                    value={selectedProductionUnitCountryCode}
                    onChange={(e) => {
                      const countryCode = e.target.value;
                      const countryObj = countries.find(c => c.code === countryCode);
                      const countryName = countryObj ? countryObj.name : '';
                      setSelectedProductionUnitCountryCode(countryCode);
                      setProductionUnitFormData({
                        ...productionUnitFormData,
                        country: countryName,
                        state: '' // Reset state when country changes
                      });
                    }}
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country.code} value={country.code}>{country.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={productionUnitFormData.postalCode}
                    onChange={(e) => setProductionUnitFormData({ ...productionUnitFormData, postalCode: e.target.value })}
                    placeholder="Postal Code"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <PhoneNumberInput
                    name="phoneNumber"
                    value={productionUnitFormData.phoneNumber}
                    onChange={(e) => setProductionUnitFormData({ ...productionUnitFormData, phoneNumber: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={productionUnitFormData.email}
                    onChange={(e) => setProductionUnitFormData({ ...productionUnitFormData, email: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowProductionUnitModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedProductionUnit ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Organization Material Modal */}
      <Modal
        show={showMaterialModal}
        onHide={() => {
          setShowMaterialModal(false);
          setMaterialCatalogDetails(null);
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedMaterial ? 'Edit Organization Material' : (materialCreateMode === 'draft' ? 'Create my own material' : 'Add from the Global Material Master')}</Modal.Title>
        </Modal.Header>
        <Form ref={materialFormRef} onSubmit={handleSaveMaterial}>
          <Modal.Body>
            {!selectedMaterial && materialCreateMode === 'draft' && (
              <div className="mb-3">
                <Button variant="link" size="sm" className="p-0" onClick={() => { setShowMaterialModal(false); setMaterialCatalogDetails(null); }}>
                  ← Back to the Global Material Master
                </Button>
              </div>
            )}

            {materialCreateMode === 'draft' && !selectedMaterial && (
              <>
                {variantOf && (
                  <Alert variant="info" className="py-2 small">
                    A variant of <strong>{variantOf.name}</strong>: its category, HSN and specification are copied. Give it its own name
                    (for example &quot;{variantOf.name}, 50 mm&quot;) and change what differs. It stays a separate material with its own stock.
                  </Alert>
                )}
                <p className="text-muted small">
                  This creates a new material immediately for your organization - ready to use right
                  away, no System Admin review needed.
                </p>
                <Form.Group className="mb-3">
                  <Form.Label>Material name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={draftMaterialForm.itemName}
                    onChange={(e) => setDraftMaterialForm({ ...draftMaterialForm, itemName: e.target.value })}
                    required
                  />
                  {!variantOf && (
                  <SimilarItemsHint
                    name={draftMaterialForm.itemName}
                    items={items}
                    isUsed={(item) => organizationMaterials.some((m) => m.item?.id === item.id)}
                    onUse={(item) => {
                      setMaterialCreateMode('existing');
                      setMaterialFormData({ ...materialFormData, itemId: String(item.id) });
                      fetchMaterialCatalogDetails(item.id);
                    }}
                  />
                  )}
                </Form.Group>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Category *</Form.Label>
                      <CategorySelect
                        itemTypes={itemTypes}
                        value={draftMaterialForm.itemTypeId}
                        disabled={!!draftMaterialForm.genericItemId}
                        onChange={(e) => {
                          if (e.target.value !== draftMaterialForm.itemTypeId) setDraftAttributes({});
                          setDraftMaterialForm({ ...draftMaterialForm, itemTypeId: e.target.value });
                        }}
                        onCreated={(newType) => {
                          setItemTypes((prev) => [...prev, newType]);
                          setDraftMaterialForm({ ...draftMaterialForm, itemTypeId: newType.id.toString() });
                          setDraftAttributes({});
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <GenericItemPicker
                      value={draftMaterialForm.genericItemId}
                      categoryId={draftMaterialForm.itemTypeId}
                      onChange={(genericItemId, picked) => {
                        // Filed under a parent item, it takes that item's category
                        if (picked && String(picked.categoryId) !== String(draftMaterialForm.itemTypeId)) setDraftAttributes({});
                        setDraftMaterialForm({ ...draftMaterialForm, genericItemId, ...(picked ? { itemTypeId: String(picked.categoryId) } : {}) });
                      }}
                    />
                  </Col>
                </Row>
                <AttributeFields
                  itemTypeId={draftMaterialForm.itemTypeId}
                  ownedByOrg={isOwnCategory(draftMaterialForm.itemTypeId)}
                  values={draftAttributes}
                  onChange={setDraftAttributes}
                />
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>HSN code (optional)</Form.Label>
                      <HsnLookupInput
                        name="hsCode"
                        value={draftMaterialForm.hsCode}
                        onChange={(e) => setDraftMaterialForm({ ...draftMaterialForm, hsCode: e.target.value })}
                        nameValue={draftMaterialForm.itemName}
                        onUseName={(itemName) => setDraftMaterialForm((f) => ({ ...f, itemName }))}
                        onSelect={(r) => {
                          const patch = categoryFromHsn(draftMaterialForm, r);
                          setDraftMaterialForm((f) => ({ ...f, hsCode: r.code, ...patch }));
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>GTIN / barcode (optional)</Form.Label>
                      <Form.Control
                        type="text"
                        value={draftMaterialForm.gtin}
                        onChange={(e) => setDraftMaterialForm({ ...draftMaterialForm, gtin: e.target.value })}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={draftMaterialForm.itemDescription}
                    onChange={(e) => setDraftMaterialForm({ ...draftMaterialForm, itemDescription: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Master SKU (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={draftMaterialForm.itemSku}
                    onChange={(e) => setDraftMaterialForm({ ...draftMaterialForm, itemSku: e.target.value })}
                  />
                </Form.Group>
              </>
            )}

            {materialCreateMode === 'existing' && (
              <div className="mb-3">
                <div className="text-muted small">Item</div>
                <strong>{materialCatalogDetails?.item?.name || items.find((x) => String(x.id) === String(materialFormData.itemId))?.name || ''}</strong>
                {(materialCatalogDetails?.item?.itemType?.name || items.find((x) => String(x.id) === String(materialFormData.itemId))?.itemType?.name) && (
                  <span className="text-muted ms-2">
                    ({materialCatalogDetails?.item?.itemType?.name || items.find((x) => String(x.id) === String(materialFormData.itemId))?.itemType?.name})
                  </span>
                )}
              </div>
            )}

            {materialCreateMode === 'existing' && loadingCatalogDetails && (
              <div className="d-flex align-items-center gap-2 mb-3 text-muted">
                <Spinner animation="border" size="sm" />
                <span>Loading material details…</span>
              </div>
            )}

            {materialCreateMode === 'existing' && !loadingCatalogDetails && materialCatalogDetails?.item && (
              <Card className="mb-3 border-info">
                <Card.Header className="py-2 bg-body-secondary d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Material Master details</strong>
                    <span className="text-muted small ms-2">
                      {canEditItemDetails(materialCatalogDetails.item)
                        ? '(created by your organization)'
                        : '(read-only, from the Global Material Master)'}
                    </span>
                  </div>
                  {canEditItemDetails(materialCatalogDetails.item) && !editingItemDetails && (
                    <Button size="sm" variant="outline-primary" onClick={handleStartEditItemDetails}>
                      Edit
                    </Button>
                  )}
                </Card.Header>
                <Card.Body className="py-3">
                  {editingItemDetails ? (
                    <>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                              type="text"
                              value={itemDetailsForm.name}
                              onChange={(e) => setItemDetailsForm({ ...itemDetailsForm, name: e.target.value })}
                            />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>Category</Form.Label>
                            <CategorySelect
                              itemTypes={itemTypes}
                              value={itemDetailsForm.itemTypeId}
                              disabled={!!itemDetailsForm.genericItemId}
                              onChange={(e) => setItemDetailsForm({
                                ...itemDetailsForm,
                                itemTypeId: e.target.value,
                                attributes: e.target.value === itemDetailsForm.itemTypeId ? itemDetailsForm.attributes : {}
                              })}
                              onCreated={(newType) => {
                                setItemTypes((prev) => [...prev, newType]);
                                setItemDetailsForm({ ...itemDetailsForm, itemTypeId: newType.id.toString() });
                              }}
                            />
                          </Form.Group>
                          <GenericItemPicker
                            value={itemDetailsForm.genericItemId}
                            categoryId={itemDetailsForm.itemTypeId}
                            excludeId={materialCatalogDetails?.item?.id}
                            onChange={(genericItemId, picked) => setItemDetailsForm({
                              ...itemDetailsForm,
                              genericItemId,
                              ...(picked && String(picked.categoryId) !== String(itemDetailsForm.itemTypeId)
                                ? { itemTypeId: String(picked.categoryId), attributes: {} } : {})
                            })}
                          />
                          <AttributeFields
                            itemTypeId={itemDetailsForm.itemTypeId}
                            ownedByOrg={isOwnCategory(itemDetailsForm.itemTypeId)}
                            values={itemDetailsForm.attributes}
                            onChange={(attributes) => setItemDetailsForm({ ...itemDetailsForm, attributes })}
                          />
                          <Form.Group className="mb-3">
                            <Form.Label>HSN code</Form.Label>
                            <HsnLookupInput
                              name="hsCode"
                              value={itemDetailsForm.hsCode}
                              onChange={(e) => setItemDetailsForm({ ...itemDetailsForm, hsCode: e.target.value })}
                            />
                          </Form.Group>
                          <Form.Group className="mb-0">
                            <Form.Label>GTIN / barcode</Form.Label>
                            <Form.Control
                              type="text"
                              value={itemDetailsForm.gtin}
                              onChange={(e) => setItemDetailsForm({ ...itemDetailsForm, gtin: e.target.value })}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-0 h-100">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={6}
                              value={itemDetailsForm.description}
                              onChange={(e) => setItemDetailsForm({ ...itemDetailsForm, description: e.target.value })}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <div className="d-flex gap-2 mt-2">
                        <Button size="sm" variant="primary" onClick={handleSaveItemDetails} disabled={savingItemDetails}>
                          {savingItemDetails ? 'Saving…' : 'Save item details'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => setEditingItemDetails(false)}
                          disabled={savingItemDetails}
                        >
                          Cancel
                        </Button>
                      </div>
                    </>
                  ) : (
                  <Row>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Name:</strong> {materialCatalogDetails.item.name}
                      </p>
                      <p className="mb-2">
                        <strong>Category:</strong>{' '}
                        {materialCatalogDetails.item.itemType?.name || '—'}
                      </p>
                      <p className="mb-2">
                        <strong>GTIN:</strong> {materialCatalogDetails.item.gtin || '—'}
                      </p>
                      <p className="mb-2">
                        <strong>HS Code:</strong> {materialCatalogDetails.item.hsCode || '—'}
                      </p>
                      <p className="mb-0">
                        <strong>Master SKU:</strong> {materialCatalogDetails.item.sku || '—'}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-1"><strong>Description</strong></p>
                      <p className="text-muted mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                        {materialCatalogDetails.item.description || '—'}
                      </p>
                    </Col>
                  </Row>
                  )}

                  <hr className="my-3" />
                  <p className="mb-2"><strong>Attributes</strong></p>
                  {(!materialCatalogDetails.attributeDetails
                    || materialCatalogDetails.attributeDetails.length === 0) ? (
                    <p className="text-muted small mb-0">
                      No attributes defined for this category.
                    </p>
                  ) : (
                    <Table size="sm" bordered responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>Attribute</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {materialCatalogDetails.attributeDetails.map((attr) => (
                          <tr key={attr.code}>
                            <td>
                              {attr.name}
                              {attr.description && (
                                <div className="small text-muted">{attr.description}</div>
                              )}
                            </td>
                            <td>{attr.displayValue ?? <span className="text-muted">—</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            )}

            <h6 className="mb-3">Organization settings</h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>SKU (Stock Keeping Unit)</Form.Label>
                  <Form.Control
                    type="text"
                    value={materialFormData.sku}
                    onChange={(e) => setMaterialFormData({ ...materialFormData, sku: e.target.value })}
                    placeholder="Organization-specific SKU"
                  />
                  <Form.Text className="text-muted">
                    Organization-specific stock keeping unit
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>MOQ (Minimum Order Quantity)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.001"
                    value={materialFormData.moq}
                    onChange={(e) => setMaterialFormData({ ...materialFormData, moq: e.target.value })}
                    placeholder="0.000"
                  />
                  <Form.Text className="text-muted">
                    Minimum quantity that must be ordered
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>SSQ (Safety Stock Quantity)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.001"
                    value={materialFormData.ssq}
                    onChange={(e) => setMaterialFormData({ ...materialFormData, ssq: e.target.value })}
                    placeholder="0.000"
                  />
                  <Form.Text className="text-muted">
                    Minimum stock level to maintain
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ROP (Reorder Point)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.001"
                    value={materialFormData.rop}
                    onChange={(e) => setMaterialFormData({ ...materialFormData, rop: e.target.value })}
                    placeholder="0.000"
                  />
                  <Form.Text className="text-muted">
                    Stock level at which to reorder
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>POQ (Preferred Order Quantity)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.001"
                    value={materialFormData.poq}
                    onChange={(e) => setMaterialFormData({ ...materialFormData, poq: e.target.value })}
                    placeholder="0.000"
                  />
                  <Form.Text className="text-muted">
                    Preferred quantity to order
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Unit of Measurement</Form.Label>
                  <Form.Select
                    value={materialFormData.unitOfMeasurementId}
                    onChange={(e) => setMaterialFormData({ ...materialFormData, unitOfMeasurementId: e.target.value })}
                  >
                    <option value="">Select UOM</option>
                    {uoms.map((uom) => (
                      <option key={uom.id} value={uom.id}>
                        {uom.symbol || uom.name} {uom.name && uom.symbol ? `(${uom.name})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Unit of measurement for quantities (MOQ, SSQ, ROP, POQ)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>GST rate (%)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    step="any"
                    list="material-gst-rates"
                    value={materialFormData.gstRate}
                    onChange={(e) => setMaterialFormData({ ...materialFormData, gstRate: e.target.value })}
                    placeholder="e.g. 5 or 18"
                  />
                  <datalist id="material-gst-rates">
                    {['0', '0.25', '1.5', '3', '5', '12', '18', '28', '40'].map((r) => <option key={r} value={r} />)}
                  </datalist>
                  <Form.Text className="text-muted">
                    Filled in on sale and purchase bill lines for this item; you can still change it on a line. Leave empty if it varies.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <OpeningStockFields
              form={openingStock}
              onChange={setOpeningStock}
              info={openingInfo}
              unitLabel={(() => {
                const u = uoms.find((x) => String(x.id) === String(materialFormData.unitOfMeasurementId));
                return u ? (u.symbol || u.name) : '';
              })()}
              currency={orgCurrencyCode}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowMaterialModal(false);
                setMaterialCatalogDetails(null);
              }}
            >
              Cancel
            </Button>
            {!selectedMaterial && materialCreateMode === 'draft' && (
              <Button
                variant="outline-primary"
                type="button"
                disabled={savingDraftMaterial}
                onClick={() => { addAnotherRef.current = true; materialFormRef.current?.requestSubmit(); }}
              >
                Save and add another
              </Button>
            )}
            <Button variant="primary" type="submit" disabled={materialCreateMode === 'draft' && savingDraftMaterial}
              onClick={() => { addAnotherRef.current = false; }}>
              {materialCreateMode === 'draft'
                ? (savingDraftMaterial ? 'Creating...' : 'Create material')
                : (selectedMaterial ? 'Update' : 'Add to my materials')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Organization Service Modal */}
      <Modal show={showServiceModal} onHide={() => setShowServiceModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedService ? 'Edit Organization Service' : 'Add Organization Service'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveService}>
          <Modal.Body>
            {!selectedService && (
              <div className="mb-3">
                {serviceCreateMode === 'existing' ? (
                  <Button variant="link" size="sm" className="p-0" onClick={() => setServiceCreateMode('draft')}>
                    Can't find it? Create a new draft service
                  </Button>
                ) : (
                  <Button variant="link" size="sm" className="p-0" onClick={() => setServiceCreateMode('existing')}>
                    ← Back to selecting an existing service
                  </Button>
                )}
              </div>
            )}

            {serviceCreateMode === 'draft' && !selectedService && (
              <>
                <p className="text-muted small">
                  This creates a new service immediately for your organization. A System Admin will
                  review it later, or merge it if it turns out to duplicate an existing service.
                </p>
                <Form.Group className="mb-3">
                  <Form.Label>Service name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={draftServiceForm.serviceName}
                    onChange={(e) => setDraftServiceForm({ ...draftServiceForm, serviceName: e.target.value })}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Service type *</Form.Label>
                  <Form.Select
                    value={draftServiceForm.serviceTypeId}
                    onChange={(e) => setDraftServiceForm({ ...draftServiceForm, serviceTypeId: e.target.value })}
                    required
                  >
                    <option value="">Select a type</option>
                    {serviceTypes.map((type) => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={draftServiceForm.serviceDescription}
                    onChange={(e) => setDraftServiceForm({ ...draftServiceForm, serviceDescription: e.target.value })}
                  />
                </Form.Group>
              </>
            )}

            {serviceCreateMode === 'existing' && (
            <Form.Group className="mb-3">
              <Form.Label>Service *</Form.Label>
              <Form.Select
                value={serviceFormData.serviceId}
                onChange={(e) => setServiceFormData({ ...serviceFormData, serviceId: e.target.value })}
                required
                disabled={!!selectedService}
              >
                <option value="">Select a service</option>
                {(selectedService
                  ? services.filter((service) => String(service.id) === String(serviceFormData.serviceId))
                  : services.filter((service) =>
                      !organizationServices.some(
                        (os) => os.service?.id != null && String(os.service.id) === String(service.id)
                      )
                    )
                ).map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name} {service.serviceType ? `(${service.serviceType.name})` : ''}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                {selectedService
                  ? 'Service cannot be changed after creation'
                  : services.filter((service) =>
                      !organizationServices.some(
                        (os) => os.service?.id != null && String(os.service.id) === String(service.id)
                      )
                    ).length === 0
                    ? 'All Services Master entries are already enabled. Use Edit on an existing row to change settings.'
                    : 'Only services not yet enabled for this organization are listed'}
              </Form.Text>
            </Form.Group>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Pricing Model</Form.Label>
                  <Form.Select
                    value={serviceFormData.pricingModel}
                    onChange={(e) => setServiceFormData({ ...serviceFormData, pricingModel: e.target.value })}
                  >
                    <option value="">Select Pricing Model</option>
                    <option value="HOURLY">Hourly</option>
                    <option value="DAILY">Daily</option>
                    <option value="FIXED">Fixed Price</option>
                    <option value="PROJECT_BASED">Project Based</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Organization-specific pricing model override
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Unit of Measurement</Form.Label>
                  <Form.Control
                    type="text"
                    value={serviceFormData.unitOfMeasurement}
                    onChange={(e) => setServiceFormData({ ...serviceFormData, unitOfMeasurement: e.target.value })}
                    placeholder="e.g., Hour, Day, Project"
                  />
                  <Form.Text className="text-muted">
                    Organization-specific unit of measurement
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{rateFieldLabel('Hourly Rate')}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={serviceFormData.hourlyRate}
                    onChange={(e) => setServiceFormData({ ...serviceFormData, hourlyRate: e.target.value })}
                    placeholder="0.00"
                  />
                  <Form.Text className="text-muted">
                    Organization-specific hourly rate
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{rateFieldLabel('Daily Rate')}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={serviceFormData.dailyRate}
                    onChange={(e) => setServiceFormData({ ...serviceFormData, dailyRate: e.target.value })}
                    placeholder="0.00"
                  />
                  <Form.Text className="text-muted">
                    Organization-specific daily rate
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{rateFieldLabel('Fixed Price')}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={serviceFormData.fixedPrice}
                    onChange={(e) => setServiceFormData({ ...serviceFormData, fixedPrice: e.target.value })}
                    placeholder="0.00"
                  />
                  <Form.Text className="text-muted">
                    Organization-specific fixed price
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Estimated Duration (Hours)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={serviceFormData.estimatedDurationHours}
                    onChange={(e) => setServiceFormData({ ...serviceFormData, estimatedDurationHours: e.target.value })}
                    placeholder="0.00"
                  />
                  <Form.Text className="text-muted">
                    Organization-specific estimated duration
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={serviceFormData.notes}
                onChange={(e) => setServiceFormData({ ...serviceFormData, notes: e.target.value })}
                placeholder="Organization-specific notes or additional information"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowServiceModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={serviceCreateMode === 'draft' && savingDraftService}>
              {serviceCreateMode === 'draft'
                ? (savingDraftService ? 'Creating...' : 'Create draft service')
                : (selectedService ? 'Update' : 'Create')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Create/Edit Role Modal */}
      <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedRole ? 'Edit Role' : 'Create Role'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveRole}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Role Name *</Form.Label>
              <Form.Control
                type="text"
                value={roleFormData.name}
                onChange={(e) => setRoleFormData({ ...roleFormData, name: e.target.value })}
                required
                placeholder="e.g., SALES_MANAGER"
              />
              <Form.Text className="text-muted">
                Use uppercase with underscores (e.g., SALES_REP, INVENTORY_MANAGER)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={roleFormData.description}
                onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
                placeholder="Describe the role's responsibilities"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={roleFormData.isActive}
                onChange={(e) => setRoleFormData({ ...roleFormData, isActive: e.target.checked })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowRoleModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedRole ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Manage Permissions Modal */}
      <Modal show={showPermissionModal} onHide={() => setShowPermissionModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Manage Permissions - {selectedRole?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {selectedRole?.isSystemRole && (
            <Alert variant="warning">
              System roles cannot have their permissions modified.
            </Alert>
          )}
          
          {Object.entries(permissionsGrouped).map(([resource, resourcePermissions]) => (
            <Card key={resource} className="mb-3">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <strong>{resource}</strong>
                  {!selectedRole?.isSystemRole && (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => toggleResourcePermissions(resource)}
                    >
                      Toggle All
                    </Button>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                <div className="row">
                  {resourcePermissions.map((permission) => (
                    <div key={permission.id} className="col-md-6 mb-2">
                      <Form.Check
                        type="checkbox"
                        id={`permission-${permission.id}`}
                        label={`${permission.action} - ${permission.description || permission.name}`}
                        checked={rolePermissions.includes(permission.id)}
                        onChange={() => togglePermission(permission.id)}
                        disabled={selectedRole?.isSystemRole}
                      />
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPermissionModal(false)}>
            Close
          </Button>
          {!selectedRole?.isSystemRole && (
            <Button variant="primary" onClick={handleSavePermissions}>
              Save Permissions
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* User Modal */}
      <Modal show={showUserModal} onHide={handleCloseUserModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingUser ? 'Edit User' : 'Create User'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveUser}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                value={userFormData.email}
                onChange={(e) => { setUserFormData({ ...userFormData, email: e.target.value }); if (!editingUser) setEmailStatus(null); }}
                onBlur={() => { if (!editingUser) checkEmail(userFormData.email); }}
                required
                autoFocus={!editingUser}
              />
              {!editingUser && emailStatus === 'checking' && <Form.Text className="text-muted">Checking...</Form.Text>}
            </Form.Group>

            {!editingUser && emailStatus?.status === 'MEMBER' && (
              <Alert variant="info" className="small">
                {emailStatus.name || 'This person'} is already a user here. Find them in the list to change their roles.
              </Alert>
            )}
            {!editingUser && emailStatus?.status === 'EXISTS' && (
              <Alert variant="info" className="small">
                This person already has an iBigDo login. They&apos;ll be added to {user?.organizationName || 'your organization'} and
                sign in with their existing password; their name and phone stay as they have them. Give them roles after adding them.
              </Alert>
            )}

            {(editingUser || !emailStatus || emailStatus.status === 'NEW') && (
              <>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>First Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={userFormData.firstName}
                        onChange={(e) => setUserFormData({ ...userFormData, firstName: e.target.value })}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={userFormData.lastName}
                        onChange={(e) => setUserFormData({ ...userFormData, lastName: e.target.value })}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <PhoneNumberInput
                    name="phoneNumber"
                    value={userFormData.phoneNumber}
                    onChange={(e) => setUserFormData({ ...userFormData, phoneNumber: e.target.value })}
                  />
                </Form.Group>
              </>
            )}

            {!editingUser && (!emailStatus || emailStatus.status === 'NEW') && (
              <Form.Group className="mb-1">
                <Form.Label>Password</Form.Label>
                <Form.Check
                  type="radio"
                  id="user-password-email"
                  name="user-password-choice"
                  label="Email them a link to choose their own password (recommended)"
                  checked={passwordChoice === 'EMAIL'}
                  onChange={() => { setPasswordChoice('EMAIL'); setUserFormData({ ...userFormData, password: '' }); }}
                />
                <Form.Check
                  type="radio"
                  id="user-password-set"
                  name="user-password-choice"
                  label="Set a password now and give it to them"
                  checked={passwordChoice === 'SET'}
                  onChange={() => setPasswordChoice('SET')}
                />
                {passwordChoice === 'SET' ? (
                  <>
                    <Form.Control
                      className="mt-2"
                      type="password"
                      value={userFormData.password}
                      onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                      required
                      autoComplete="new-password"
                      placeholder="Choose a strong password"
                    />
                    <PasswordHint password={userFormData.password} />
                  </>
                ) : (
                  <Form.Text className="text-muted d-block">
                    The link works once, for 7 days. You won&apos;t need to know their password.
                  </Form.Text>
                )}
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            {editingUser && (
              <Button variant="outline-secondary" className={twoFactorOn.includes(editingUser.id) ? '' : 'me-auto'} disabled={sendingLink} onClick={sendSetPasswordLink}>
                {sendingLink ? 'Sending...' : 'Email a set-password link'}
              </Button>
            )}
            {editingUser && twoFactorOn.includes(editingUser.id) && (
              <Button variant="outline-danger" className="me-auto" onClick={resetTwoFactor} data-testid="reset-two-factor">
                Reset two-step sign-in
              </Button>
            )}
            <Button variant="secondary" onClick={handleCloseUserModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={!editingUser && (emailStatus === 'checking' || emailStatus?.status === 'MEMBER')}>
              {editingUser ? 'Update' : emailStatus?.status === 'EXISTS' ? 'Add to organization' : 'Create user'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Role Assignment Modal */}
      <Modal show={showRoleAssignmentModal} onHide={handleCloseRoleAssignmentModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Manage Roles for {selectedUserForRoleAssignment && `${selectedUserForRoleAssignment.firstName} ${selectedUserForRoleAssignment.lastName}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUserForRoleAssignment && (
            <>
              <div className="mb-4">
                <h6>Current Roles</h6>
                {selectedUserForRoleAssignment.roles && selectedUserForRoleAssignment.roles.length > 0 ? (
                  <div>
                    {selectedUserForRoleAssignment.roles.map((role, idx) => {
                      const roleObj = typeof role === 'object' ? role : null;
                      const roleId = roleObj?.id;
                      const roleName = typeof role === 'string' ? role : (roleObj?.name || 'Unknown');
                      const locked = isOrgAdminRole(roleName) ? orgAdminRemovalBlock(selectedUserForRoleAssignment) : null;
                      return (
                        <Badge key={idx} bg="primary" className="me-2 mb-2" style={{ fontSize: '0.9rem', padding: '0.5rem' }}
                          title={locked || undefined}>
                          {getRoleLabel(roleObj || roleName)}
                          {locked && <i className="bi bi-lock-fill ms-2" aria-label="Can't be removed here" />}
                          {roleId && !locked && (
                            <button
                              type="button"
                              className="btn-close btn-close-white ms-2"
                              style={{ fontSize: '0.7rem' }}
                              onClick={() => handleRemoveRoleFromUser(roleId)}
                              aria-label="Remove role"
                            ></button>
                          )}
                        </Badge>
                      );
                    })}
                    {selectedUserForRoleAssignment.roles.some(isOrgAdminRole)
                      && orgAdminRemovalBlock(selectedUserForRoleAssignment) && (
                      <div className="small text-muted mt-1">
                        <i className="bi bi-lock me-1" aria-hidden="true" />
                        {orgAdminRemovalBlock(selectedUserForRoleAssignment)}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted">No roles assigned</p>
                )}
              </div>

              <div>
                <h6>Available Roles</h6>
                {roles.length > 0 ? (
                  <div>
                    {roles
                      .filter(role => {
                        // Filter out roles the user already has
                        if (!selectedUserForRoleAssignment.roles) return true;
                        return !selectedUserForRoleAssignment.roles.some(userRole => {
                          const userRoleId = typeof userRole === 'object' && userRole.id ? userRole.id : null;
                          return userRoleId === role.id;
                        });
                      })
                      .map((role) => (
                        <div key={role.id} className="mb-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleAssignRoleToUser(role.id)}
                            className="w-100 text-start"
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <strong>{getRoleLabel(role)}</strong>
                                {role.description && (
                                  <div className="text-muted small">{role.description}</div>
                                )}
                              </div>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
                              </svg>
                            </div>
                          </Button>
                        </div>
                      ))}
                    {roles.filter(role => {
                      if (!selectedUserForRoleAssignment.roles) return false;
                      return !selectedUserForRoleAssignment.roles.some(userRole => {
                        const userRoleId = typeof userRole === 'object' && userRole.id ? userRole.id : null;
                        return userRoleId === role.id;
                      });
                    }).length === 0 && (
                      <p className="text-muted">All available roles have been assigned</p>
                    )}
                  </div>
                ) : (
                  <p className="text-muted">No roles available. Create roles in the Roles tab first.</p>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseRoleAssignmentModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Print View Modal */}
      <Modal show={showPrintModal} onHide={() => setShowPrintModal(false)} size="xl" className="print-modal">
        <Modal.Header className="print-header">
          <Modal.Title>
            {printType === 'USER' && 'User'}
            {printType === 'ROLE' && 'Role'}
            {printType === 'WAREHOUSE' && 'Warehouse'}
            {printType === 'RETAIL_STORE' && 'Retail Store'}
            {printType === 'PRODUCTION_UNIT' && 'Production Unit'}
            {printType === 'MATERIAL' && 'Organization Material'}
            {printType === 'SERVICE' && 'Organization Service'}
            {printType === 'PRODUCT' && 'Product'}
          </Modal.Title>
          <div className="print-actions">
            <Button variant="primary" onClick={handlePrint} className="me-2">Print</Button>
            <Button variant="secondary" onClick={() => setShowPrintModal(false)}>Close</Button>
          </div>
        </Modal.Header>
        <Modal.Body className="print-body">
          <OrgPrintChrome>
          {printType === 'USER' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>USER</h2>
                <p className="text-muted"><strong>{printData.email}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>First Name:</strong> {printData.firstName || '-'}</p>
                  <p><strong>Last Name:</strong> {printData.lastName || '-'}</p>
                  <p><strong>Phone:</strong> {printData.phoneNumber || '-'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Active:</strong> {printData.isActive != null ? (printData.isActive ? 'Yes' : 'No') : '-'}</p>
                </Col>
              </Row>
            </div>
          )}
          {printType === 'ROLE' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>ROLE</h2>
                <p className="text-muted"><strong>{printData.name}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Description:</strong> {printData.description || '-'}</p>
                  <p><strong>System Role:</strong> {printData.isSystemRole ? 'Yes' : 'No'}</p>
                  <p><strong>Active:</strong> {printData.isActive ? 'Yes' : 'No'}</p>
                </Col>
                <Col md={6}>
                  {printData.permissions && printData.permissions.length > 0 && (
                    <p><strong>Permissions:</strong> {printData.permissions.map(p => typeof p === 'object' ? p.name : p).join(', ')}</p>
                  )}
                </Col>
              </Row>
            </div>
          )}
          {printType === 'WAREHOUSE' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>WAREHOUSE</h2>
                <p className="text-muted"><strong>{printData.name}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Description:</strong> {printData.description || '-'}</p>
                  <p><strong>Address:</strong> {printData.address || '-'}</p>
                  <p><strong>City:</strong> {printData.city || '-'}</p>
                  <p><strong>State:</strong> {printData.state || '-'}</p>
                  <p><strong>Country:</strong> {printData.country || '-'}</p>
                  <p><strong>Postal Code:</strong> {printData.postalCode || '-'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Phone:</strong> {printData.phoneNumber || '-'}</p>
                  <p><strong>Email:</strong> {printData.email || '-'}</p>
                  <p><strong>Active:</strong> {printData.isActive ? 'Yes' : 'No'}</p>
                </Col>
              </Row>
            </div>
          )}
          {printType === 'RETAIL_STORE' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>RETAIL STORE</h2>
                <p className="text-muted"><strong>{printData.name}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Description:</strong> {printData.description || '-'}</p>
                  <p><strong>Address:</strong> {printData.address || '-'}</p>
                  <p><strong>City:</strong> {printData.city || '-'}</p>
                  <p><strong>State:</strong> {printData.state || '-'}</p>
                  <p><strong>Country:</strong> {printData.country || '-'}</p>
                  <p><strong>Postal Code:</strong> {printData.postalCode || '-'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Phone:</strong> {printData.phoneNumber || '-'}</p>
                  <p><strong>Email:</strong> {printData.email || '-'}</p>
                  <p><strong>Active:</strong> {printData.isActive ? 'Yes' : 'No'}</p>
                </Col>
              </Row>
            </div>
          )}
          {printType === 'PRODUCTION_UNIT' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>PRODUCTION UNIT</h2>
                <p className="text-muted"><strong>{printData.name}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Description:</strong> {printData.description || '-'}</p>
                  <p><strong>Address:</strong> {printData.address || '-'}</p>
                  <p><strong>City:</strong> {printData.city || '-'}</p>
                  <p><strong>State:</strong> {printData.state || '-'}</p>
                  <p><strong>Country:</strong> {printData.country || '-'}</p>
                  <p><strong>Postal Code:</strong> {printData.postalCode || '-'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Phone:</strong> {printData.phoneNumber || '-'}</p>
                  <p><strong>Email:</strong> {printData.email || '-'}</p>
                  <p><strong>Active:</strong> {printData.isActive ? 'Yes' : 'No'}</p>
                </Col>
              </Row>
            </div>
          )}
          {printType === 'MATERIAL' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>ORGANIZATION MATERIAL</h2>
                <p className="text-muted">
                  <strong>
                    {printData.catalogDetails?.item?.name || printData.item?.name || '-'}
                  </strong>
                </p>
              </div>

              <h5 className="mb-3">Material Master</h5>
              {printData.catalogDetailsLoading ? (
                <p className="text-muted">Loading material details…</p>
              ) : (
                <>
                  <Row className="mb-3">
                    <Col md={6}>
                      <p>
                        <strong>Category:</strong>{' '}
                        {printData.catalogDetails?.item?.itemType?.name
                          || printData.item?.itemType?.name
                          || '-'}
                      </p>
                      <p>
                        <strong>GTIN:</strong>{' '}
                        {printData.catalogDetails?.item?.gtin || printData.item?.gtin || '-'}
                      </p>
                      <p>
                        <strong>HS Code:</strong>{' '}
                        {printData.catalogDetails?.item?.hsCode || printData.item?.hsCode || '-'}
                      </p>
                      <p>
                        <strong>Master SKU:</strong>{' '}
                        {printData.catalogDetails?.item?.sku || printData.item?.sku || '-'}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Description:</strong></p>
                      <p style={{ whiteSpace: 'pre-wrap' }}>
                        {printData.catalogDetails?.item?.description
                          || printData.item?.description
                          || '-'}
                      </p>
                    </Col>
                  </Row>

                  <h6 className="mb-2">Attributes</h6>
                  {(!printData.catalogDetails?.attributeDetails
                    || printData.catalogDetails.attributeDetails.length === 0) ? (
                    <p className="text-muted mb-3">No attributes defined for this category.</p>
                  ) : (
                    <Table size="sm" bordered className="mb-4">
                      <thead>
                        <tr>
                          <th>Attribute</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {printData.catalogDetails.attributeDetails.map((attr) => (
                          <tr key={attr.code}>
                            <td>
                              {attr.name}
                              {attr.description && (
                                <div className="small text-muted">{attr.description}</div>
                              )}
                            </td>
                            <td>{attr.displayValue || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </>
              )}

              <h5 className="mb-3">Organization Settings</h5>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Org SKU:</strong> {printData.sku || '-'}</p>
                  <p><strong>MOQ:</strong> {printData.moq != null && printData.moq !== '' ? printData.moq : '-'}</p>
                  <p><strong>SSQ:</strong> {printData.ssq != null && printData.ssq !== '' ? printData.ssq : '-'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>ROP:</strong> {printData.rop != null && printData.rop !== '' ? printData.rop : '-'}</p>
                  <p><strong>POQ:</strong> {printData.poq != null && printData.poq !== '' ? printData.poq : '-'}</p>
                  <p>
                    <strong>UOM:</strong>{' '}
                    {printData.unitOfMeasurement
                      ? (printData.unitOfMeasurement.symbol || printData.unitOfMeasurement.name)
                      : '-'}
                  </p>
                </Col>
              </Row>
            </div>
          )}
          {printType === 'SERVICE' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>ORGANIZATION SERVICE</h2>
                <p className="text-muted"><strong>{printData.service?.name || '-'}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Pricing Model:</strong> {printData.pricingModel || '-'}</p>
                  <p><strong>Hourly Rate:</strong> {printData.hourlyRate != null ? formatOrgMoney(printData.hourlyRate) : '-'}</p>
                  <p><strong>Daily Rate:</strong> {printData.dailyRate != null ? formatOrgMoney(printData.dailyRate) : '-'}</p>
                  <p><strong>Fixed Price:</strong> {printData.fixedPrice != null ? formatOrgMoney(printData.fixedPrice) : '-'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Estimated Duration (hrs):</strong> {printData.estimatedDurationHours || '-'}</p>
                  <p><strong>Unit of Measurement:</strong> {printData.unitOfMeasurement || '-'}</p>
                  {printData.notes && <p><strong>Notes:</strong> {printData.notes}</p>}
                </Col>
              </Row>
            </div>
          )}
          {printType === 'PRODUCT' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>PRODUCT</h2>
                <p className="text-muted"><strong>{printData.name || printData.item?.name || '-'}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Item (Material Master):</strong> {printData.item?.name || '-'}</p>
                  <p><strong>SKU:</strong> {printData.sku || '-'}</p>
                  <p><strong>Description:</strong> {printData.description || '-'}</p>
                  <p><strong>Default sale price:</strong> {formatProductSalePrice(printData.price)}</p>
                  <p><strong>Cost Price:</strong> {printData.costPrice != null ? formatOrgMoney(printData.costPrice) : '-'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Category:</strong> {printData.category || '-'}</p>
                  <p><strong>Brand:</strong> {printData.brand || '-'}</p>
                </Col>
              </Row>
            </div>
          )}
                  </OrgPrintChrome>
        </Modal.Body>
      </Modal>

      {/* Help Pane */}
    </Container>
  );
}

export default OrgAdmin;

